/**********************************
 *  Some elementaries functions     *
 **********************************/
import React from "react";

import { createTheme } from "@mui/material/styles";
import imageCompression from "browser-image-compression";
import { formatDistanceToNow } from "date-fns";
import * as _ from "lodash";
import { forEach } from "lodash";
import get from "lodash/get";
import uniqueId from "lodash/uniqueId";
import moment from "moment-timezone";
import mongoose from "mongoose";
import * as locales from "react-date-range/dist/locale";
import { toArray } from "react-emoji-render";
import { format, isValid } from "date-fns";

import BlackTooltip from "@components/tooltip/BlackTooltip";

import {
  DocumentStorage,
  EmployerEmplacement,
  Employer as EmployerService
} from "@services/all.services";

import {
  congeCategoriesPossibleValue,
  defaultUserImage,
  FileType,
  INFO_ACTION,
  ERROR,
  SUCCESS_ACTION,
  WARNING_ACTION,
  REGEX_NOT_NUMBER,
  REGEX_PHONE,
  REGEX_EMAIL,
  statusColors,
  REGEX_CHECK_LINKS,
  VACANCE,
  ABSENCE,
  MALADIE,
  NOT_INFORMED,
  COLOR_NO_INFORMATION,
  DEFINITIVEMENT_INACTIF
} from "./_constants";

export const timePickerTheme = createTheme({
  components: {
    MuiIconButton: {
      styleOverrides: {
        sizeMedium: {
          color: "#292929",
          backgroundColor: "#F9FAFC"
        }
      }
    }
  },
  palette: {
    primary: {
      main: "#fcb698",
      contrastText: "#fff"
    }
  }
});

export const customizeError = (err) => {
  let err_message = "Erreur inconnu!";
  if (err) {
    if (err.error) {
      err = err.error;
    }
    if (err.message) {
      err_message = err.message;
    } else if (err.msg) {
      err_message = err.msg;
    } else if (typeof err.data?.error === "string") {
      err_message = err.data.error;
    }
  }
  let errorCustomized = {
    success: false,
    error: err,
    status: false,
    message: err_message
  };
  return errorCustomized;
};

export const previewDate = (date, i18n) => {
  if (!date || date === "Invalid date") return "-";

  const parsedDate = moment(getDateToRender(date));

  if (!parsedDate.isValid()) return "-";

  return parsedDate.format(
    i18n.language === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY"
  );
}

export const parseValidNumber = (_number, _fixed = 2) =>
  parseFloat(Number(_number).toFixed(_fixed));
// Get url of bucket on Wasabi
export const _s3 = (path) => {
  return process.env.REACT_APP_BUCKET_URL + path;
};

//check user id
export const checkUserId = (userId) => {
  if (!userId) {
    userId = localStorage.getItem("userId");
  }
  return userId;
};

//check enterprise id
export const checkEnterpriseId = (enterpriseId) => {
  if (!enterpriseId) {
    enterpriseId = localStorage.getItem("enterpriseId");
  }
  return enterpriseId;
};

//Check user permissions
export const checkPermission = (userPermissions, permissionLabel) => {
  let permitted = false;
  for (let i in userPermissions) {
    if (userPermissions[i]?.[0]) {
      let labelIndex = userPermissions[i][0].indexOf(permissionLabel);
      if (labelIndex >= 0) {
        permitted = true;
      }
    }
  }
  return permitted;
};

// Check user permissions tabs v0.2
export const checkPermissionTab = (permittedTabs, permissionLabel) => {
  if (!Array.isArray(permittedTabs)) {
    return false;
  }
  return Array.isArray(permissionLabel)
    ? permissionLabel.some((label) => permittedTabs.includes(label))
    : permittedTabs.includes(permissionLabel);
};

//Check api response data
export const getResponseData = (response, single = false) => {
  if (response?.status === 200) {
    if (single && response.data.data?.[0]) return response.data.data[0];
    else return response.data.data;
  } else {
    return Promise.reject(customizeError(response.data));
  }
};

// Check api response data and track error
export const getResponseQuery = (query) => {
  return query
    .then((response) => getResponseData(response))
    .catch((err) => {
      return Promise.reject(customizeError(err));
    });
};

//search employers by key name
export const getEmployersSearchResult = (allEmployers, searchKey) => {
  let result = [];
  if (allEmployers?.[0]?.Employer?.personnalInformation?.[0] && searchKey) {
    for (const employer of allEmployers) {
      let employerName = getFullNameByEmployer(employer.Employer);
      let keyIndex = employerName
        .toLowerCase()
        .indexOf(searchKey.toLowerCase());
      if (keyIndex >= 0) {
        result.push(employer);
      }
    }
  } else {
    result = allEmployers ? allEmployers : [];
  }
  return result;
};

//get fullname of employer in employer dropdowns
export const getEmployerFullName = (employer) =>
  (employer?.firstName ? employer.firstName + " " : "") +
  (employer?.name ? employer.name : "");

export const getEmplacementName = (emplacement) => {
  let name = "";
  if (emplacement?.name) {
    name += emplacement.name;
  }
  if (emplacement?.name) {
    name += (name ? " " : "") + emplacement.name;
  }
  return name;
};

//get fullname of employer from Employer personal info
export const getFullNameByEmployer = (Employer) =>
  (Employer?.legalFirstName ? Employer.legalFirstName + " " : "") +
  (Employer?.legalName ? Employer.legalName : "");

//get dates and hours in the header of  popup for creating conge
export const getDatesAndHoursHeading = (startDate, endDate) => {
  let datesHeader = "--";
  let hoursHeader = "--";
  if (
    startDate &&
    endDate &&
    moment(startDate).isValid() &&
    moment(endDate).isValid()
  ) {
    startDate = moment(startDate);
    endDate = moment(endDate);
    if (startDate.format("DD/MM/YYYY") === endDate.format("DD/MM/YYYY")) {
      datesHeader = startDate.format("DD MMMM YYYY");
    } else {
      let startDateFormat = "DD MMMM YYYY";
      let endDateFormat = "DD MMMM YYYY";
      if (startDate.format("MM/YYYY") === endDate.format("MM/YYYY")) {
        startDateFormat = "DD";
        endDateFormat = "DD MMMM YYYY";
      } else if (startDate.format("YYYY") === endDate.format("YYYY")) {
        startDateFormat = "DD MMMM";
        endDateFormat = "DD MMMM YYYY";
      }
      datesHeader =
        startDate.format(startDateFormat) + "-" + endDate.format(endDateFormat);
    }
    hoursHeader = startDate.format("HH:mm") + "-" + endDate.format("HH:mm");
  } else if (startDate && moment(startDate).isValid()) {
    startDate = moment(startDate);
    datesHeader = startDate.format("DD MMMM YYYY").toUpperCase();
    hoursHeader = startDate.format("HH:mm");
  } else if (endDate && moment(endDate).isValid()) {
    endDate = moment(endDate);
    datesHeader = endDate.format("DD MMMM YYYY").toUpperCase();
    hoursHeader = endDate.format("HH:mm");
  }
  return { dates: datesHeader, hours: hoursHeader };
};

//get the employer conge remaining day by the value of category
export const getEmployerRemainingDayByCategory = (employer, category) => {
  let availableDay = 0;
  if (employer?.holidayRemaining && category) {
    let holidayRemaining = employer?.holidayRemaining;
    switch (congeCategoriesPossibleValue.indexOf(category?.value)) {
      case 0: //category === "Absence"
        availableDay = checkRemaingDay(holidayRemaining.hoursMiss);
        break;
      case 1: //category === "Vacances"
        // availableDay = holidayRemaining.hoursIllimited
        //   ? Infinity
        //   : checkRemaingDay(holidayRemaining.hoursHoliday);
        availableDay = checkRemaingDay(holidayRemaining.hoursHoliday);
        break;
      case 2: //category === "Maladie"
        availableDay = checkRemaingDay(holidayRemaining.hoursSick);
        break;
      default:
        availableDay = 0;
    }
  }
  return availableDay;
};
const checkRemaingDay = (remainingDay) => {
  return parseFloat(remainingDay) ? remainingDay : 0;
};
export const getUserPhoto = (userPhoto) => {
  let photo = "";
  if (userPhoto?.profileImg) {
    photo = userPhoto.profileImg;
  } else if (userPhoto?.[0]?.profileImg) {
    photo = userPhoto[0].profileImg;
  }
  return getUrlPhoto(photo);
};
export const getUrlPhoto = (databaseUrl) => {
  return databaseUrl || defaultUserImage;
};
export const parseNumberToInt = (value) => {
  const parsedValue = parseInt(value);
  return isNaN(parsedValue) ? "" : Math.abs(parsedValue);
};
export const parseNumberToFloat = (value) => {
  if (value && value.indexOf(".") === value.length - 1) {
    return value;
  } else {
    const parsedValue = parseFloat(value);
    return isNaN(parsedValue) ? "" : Math.abs(parsedValue);
  }
};
export const formatNumberWithDecimals = (value, decimalPlaces) => {
  const regex = new RegExp(`[^\\d\\.${decimalPlaces > 0 ? "" : "\\-"}]`, "g");
  const parsedValue = value.replace(regex, "");

  if (
    parsedValue.includes(".") &&
    parsedValue.indexOf(".") !== parsedValue.lastIndexOf(".")
  ) {
    const parts = parsedValue.split(".");
    const cleanedValue = `${parts[0]}.${parts.slice(1).join("")}`;
    return cleanedValue;
  } else {
    return parsedValue;
  }
};

export const getFileTitle = (key) => {
  if (key?.length) {
    return key.substring(key.lastIndexOf("/") + 1);
  }
  return key;
};
export const getExtensionFile = (key) => {
  const fileTitle = getFileTitle(key);
  let fileExtension = "";
  if (fileTitle) {
    fileExtension = fileTitle.substring(fileTitle.lastIndexOf(".") + 1);
  }
  return fileExtension;
};
export const debounce = (callback, delay = 1000) => {
  var time;
  return (...args) => {
    clearTimeout(time);
    time = setTimeout(() => {
      callback(...args);
    }, delay);
  };
};
export const getFileName = (key) => {
  const fileTitle = getFileTitle(key);
  let fileName = "";
  if (fileTitle) {
    fileName = fileTitle.substring(0, fileTitle.lastIndexOf("."));
  }
  return fileName;
};

export const twoDigit = (number) => {
  return number.toString().padStart(2, "0");
};

// export const getParentFolderByKeyAndEmployer = (parentKey, employerId) => new Promise ((resolve, reject) => {
export const getParentFolderByKeyAndEmployer = async (
  parentKey,
  employerId
) => {
  var dataParentFolder;
  if (parentKey !== "") {
    await DocumentStorage.getParentByKey({
      key: btoa(parentKey),
      // key: parentKey,
      employerId
    })
      .then((parentFolderResponse) => {
        if (parentFolderResponse.data?.data) {
          const parentFolder = parentFolderResponse.data.data;
          const subDataParentFolder = {
            parent: parentFolder._id,
            visibility: parentFolder.isVisible
          };
          // resolve(subDataParentFolder);
          dataParentFolder = subDataParentFolder;
        }
      })
      .catch((error) => {
        console.log(error);
        return null;
      });
    return dataParentFolder;
  } else {
    // resolve({});
    return null;
  }
};

export const getEmployeesGroupEmplacement = async ({
  Employees = [],
  Emplacements = [],
  Groupes = []
}) => {
  let employeesListToCheck = Employees.map((employee) => employee.id);
  if (Groupes.length || Emplacements.length) {
    const responseEmployees = await EmployerService.getByGroupsEmplacements({
      Employees: Employees.map((employee) => employee.id),
      Emplacements: Emplacements.map((emplacement) => emplacement._id),
      Groupe: Groupes
    });
    if (responseEmployees?.data?.data?.length) {
      employeesListToCheck = responseEmployees.data?.data;
    }
  }
  return employeesListToCheck;
};

export const getObjectEmployeesGroupEmplacement = async ({
  Employees = [],
  Emplacements = [],
  Groupes = [],
  employeList = []
}) => {
  let employeesListToCheck = await getEmployeesGroupEmplacement({
    Employees,
    Emplacements,
    Groupes
  });
  return employeList.filter((employee) =>
    employeesListToCheck.includes(employee.id)
  );
};

// });
export const removeDuplicatesItem = (array) => {
  const result = [];
  for (let i = 0; i < array.length; i++) {
    let exists = false;
    for (let j = 0; j < result.length; j++) {
      if (array[i] === result[j]) {
        exists = true;
        break;
      }
    }
    if (!exists) {
      result.push(array[i]);
    }
  }
  return result;
};

// return format UTC+ time zone
export const getUtcTimeZone = (timeZone) => {
  if (timeZone) {
    if (timeZone.indexOf("UTC") < 0) {
      let hourTimestamps = moment.tz(timeZone).format("Z");
      hourTimestamps = "UTC" + hourTimestamps + " " + timeZone;
      return hourTimestamps;
    } else {
      return timeZone;
    }
  } else {
    return "";
  }
};

//converting data object filemanager
export const dataConvertExternalLink = (data, sourceKey, isOnMyFile) => {
  data.sort(function compare(a, b) {
    if (a.key < b.key) return -1;
    if (a.key > b.key) return 1;
    return 0;
  });
  let array = [];
  for (const element of data) {
    if (
      element.key.indexOf(sourceKey) === -1 &&
      element.type === FileType["folder"] &&
      !element.isALink
    )
      array.push(element.key.split("/"));
  }
  for (let element of array) {
    if (element[element.length - 1] === "") {
      element.splice(-1, 1);
    }
    element.unshift("Documents");
  }
  let tree = arrangeIntoTreeExternalLink(array, isOnMyFile);
  tree = tree.length ? tree : [{ label: "Documents", value: "" }];
  return tree;
};
const arrangeIntoTreeExternalLink = (paths, isOnMyFile) => {
  let tree = [];
  for (const path of paths) {
    let currentLevel = tree;
    let value = "";
    for (let j = 0; j < path.length; j++) {
      let part = path[j];
      if (j > 0) value += path[j] + "/";
      let existingPath = findWhere(currentLevel, "label", part);
      if (existingPath) {
        if (!existingPath.children) {
          existingPath.children = [];
        }
        if (!isOnMyFile) currentLevel = existingPath.children;
      } else {
        let newPart = {
          label: part,
          value: value
        };
        currentLevel.push(newPart);
        if (j < path.length - 1) {
          newPart.children = [];
          currentLevel = newPart.children;
        }
      }
    }
  }
  tree = filterTreeChildren(tree);
  return tree;
};
export const dataConvert = (data, sourceKey, isOnMyFile) => {
  data.sort(function compare(a, b) {
    if (a.key < b.key) return -1;
    if (a.key > b.key) return 1;
    return 0;
  });
  let array = [];
  for (const element of data) {
    if (
      element.key.indexOf(sourceKey) === -1 &&
      element.type === FileType["folder"] &&
      !element.isALink
    )
      array.push(element.key.split("/"));
  }
  for (let element of array) {
    if (element[element.length - 1] === "") {
      element.splice(-1, 1);
    }
    element.unshift("Documents");
  }
  let tree = arrangeIntoTree(array, isOnMyFile);
  tree = tree.length ? tree : [{ label: "Documents", value: "" }];
  return tree;
};

const arrangeIntoTree = (paths, isOnMyFile) => {
  let tree = [];
  for (const path of paths) {
    let currentLevel = tree;
    let value = "";
    for (let j = 0; j < path.length; j++) {
      let part = path[j];
      if (j > 0) value += path[j] + "/";
      let existingPath = findWhere(currentLevel, "label", part);
      if (existingPath) {
        if (!existingPath.children) {
          existingPath.children = [];
        }
        if (!isOnMyFile) currentLevel = existingPath.children;
      } else {
        let newPart = {
          label: part,
          value: value
        };
        currentLevel.push(newPart);
        if (j < path.length - 1) {
          newPart.children = [];
          currentLevel = newPart.children;
        }
      }
    }
  }
  tree = filterTreeChildren(tree);
  return tree;
};
const findWhere = (array, key, value) => {
  let t = 0;
  while (t < array.length && array[t][key] !== value) {
    t++;
  }
  if (t < array.length) {
    return array[t];
  } else {
    return false;
  }
};

const filterTreeChildren = (tree) => {
  return tree?.map((node) => {
    if (node?.children?.length) {
      node.children = node?.children.filter((child) => child.value !== "/");
    }
    return node;
  });
};

export const formatDate = (date) => {
  let dateFormatted = "";
  if (date && moment(date).isValid()) {
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();
    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;
    dateFormatted = [year, month, day].join("-");
  }
  return dateFormatted;
};
export const getCommentsLength = (NewsComment, language) => {
  let Comments = language === "en" ? "Comment" : "Commentaire";
  let comment = "0 " + Comments;
  if (NewsComment?.length) {
    if (NewsComment?.[0]?.name) {
      const newsCommentsLength = NewsComment.length;
      if (newsCommentsLength === 1) {
        comment = "1 " + Comments;
        return comment;
      } else {
        comment = newsCommentsLength + " " + Comments + "s";
        return comment;
      }
    }
  }
  return comment;
};
export const getLikesLength = (likeState, language) => {
  let like = language === "en" ? "Like" : "J'aime";
  let likes = language === "en" ? "Likes" : "J'aimes";
  let likeReturned = "0 " + like;
  if (likeState === 1) {
    likeReturned = "1 " + like;
  } else if (likeState === 0) {
    likeReturned = "0 " + like;
  } else {
    likeReturned = likeState + " " + likes;
  }
  return likeReturned;
};
export const likeCount = (newsLikes) => {
  let likeState = 0;
  if (newsLikes?.length) {
    // const newsLikeLength = NewsLike.length
    for (let newsLike of newsLikes) {
      if (newsLike.statusLike) {
        likeState++;
      }
    }
  }
  return likeState;
};

export const compareEmployersFullName = (employerA, employerB) => {
  const AFullName = getFullNameByEmployer(employerA.personnalInformation?.[0]);
  const BFullName = getFullNameByEmployer(employerB.personnalInformation?.[0]);
  if (AFullName < BFullName) {
    return -1;
  }
  if (AFullName > BFullName) {
    return 1;
  }
  return 0;
};

export const getFilterTabs = (params) => {
  const Tabs = [];

  for (let i = 0; i < params.length; i++) {
    let exists = false;
    for (let j = 0; j < Tabs.length; j++) {
      if (params[i]._id === Tabs[j]._id) {
        exists = true;
        break;
      }
    }
    if (!exists) {
      Tabs.push(params[i]);
    }
  }

  return Tabs;
};
export const getFilterTabsForGroup = (params) => {
  const Tabs = [];

  for (let i = 0; i < params.length; i++) {
    let exists = false;
    for (let j = 0; j < Tabs.length; j++) {
      if (params[i] === Tabs[j]) {
        exists = true;
        break;
      }
    }
    if (!exists) {
      Tabs.push(params[i]);
    }
  }

  return Tabs;
};

export const compareFilesAndFolder = (dataFiles) => {
  dataFiles.sort(function compare(a, b) {
    let keyA = a.key.split("/");
    let keyB = b.key.split("/");
    const isFileA = keyA[keyA.length - 1] !== "";
    const isFileB = keyB[keyB.length - 1] !== "";
    if (isFileA && !isFileB) return 1;
    else if (!isFileA && isFileB) return -1;
    else {
      if (!isFileA && !isFileB) {
        const isALinkA = a.isALink || false;
        const isALinkB = b.isALink || false;

        if (isALinkA && !isALinkB) return 1;
        if (!isALinkA && isALinkB) return -1;
      }
      const nameA = a.originalName || a.name || a.key;
      const nameB = b.originalName || b.name || b.key;

      return nameA.localeCompare(nameB, undefined, {
        numeric: true,
        sensitivity: "base"
      });
    }
  });
};

export const compareByName = (name1, name2) => {
  if (
    (
      name1?.Employer?.personnalInformation?.[0]?.legalFirstName +
      " " +
      name1?.Employer?.personnalInformation?.[0]?.legalName
    ).toLowerCase() <
    (
      name2?.Employer?.personnalInformation?.[0]?.legalFirstName +
      " " +
      name2?.Employer?.personnalInformation?.[0]?.legalName
    ).toLowerCase()
  ) {
    return -1;
  } else {
    return 1;
  }
};

export const getIdForSplitURL = (params) => {
  var url = window.location.href;
  let urlSplit = url.split("/");
  let id = urlSplit[params];
  return id;
};

export const getHourTimezone = (hour, timezone) => {
  let dateTimezone = null;
  if (hour?.date && moment(hour?.date).isValid()) {
    if (!timezone || !hour?.timezone) {
      dateTimezone = new Date(hour.date);
    } else {
      let dateWithTimezoneEmploye = moment
        .tz(hour.date, hour.timezone)
        .format();
      dateTimezone = new Date(
        moment(dateWithTimezoneEmploye).tz(timezone).format("YYYY-MM-DD HH:mm")
      ); // date après conversion
    }
  }
  return dateTimezone;
};

export const getTimezoneName = (timezone) => {
  if (timezone && timezone.split(" ")?.length > 0) {
    let timeZone = timezone.split(" ");
    if (timeZone?.[0]) {
      if (timeZone?.[1]) {
        return timeZone[1];
      } else {
        return timeZone[0];
      }
    }
  } else {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
};

export const getDateWithoutTimezome = (date) => {
  if (moment(date).isValid()) {
    return moment(date)
      .utcOffset(moment().utcOffset())
      .tz("UTC", true)
      .set({ s: 0, ms: 0 });
  }
  return date;
};
export const getDateWithoutTimezone = (date) => {
  if (moment(date).isValid()) {
    return moment(date).startOf("day");
  }
  return date;
};
// export const validateDateIntoAPeriod = (date, start, end) => {
//   if (
//     moment(date).isValid() &&
//     moment(start).isValid() &&
//     moment(end).isValid()
//   ) {
//     return getDateWithoutTimezome(date).isBetween(
//       moment(start),
//       moment(end),
//       null,
//       "[]"
//     );
//   }
//   return date;
// };
// export const validateDateIntoAPeriod = (date, start, end) => {
//   if (
//     moment(date).isValid() &&
//     moment(start).isValid() &&
//     moment(end).isValid()
//   ) {
//     const _date = getDateWithoutTimezone(date);
//     const _start = getDateWithoutTimezone(start);
//     const _end = getDateWithoutTimezone(end);

//     return _date.isBetween(_start, _end, "day", "[]");
//   }
//   return false;
// };

export const getTimezoneByEmployeId = async (employeId) => {
  const valueEmplacement = await EmployerEmplacement.getByEmployer(employeId);
  let listEmplacements = valueEmplacement?.data?.data?.[0]?.Emplacement;
  if (listEmplacements) {
    listEmplacements.sort((emplacement1, emplacement2) => {
      if (emplacement1._id < emplacement2._id) return -1;
      if (emplacement1._id > emplacement2._id) return 1;
      return 0;
    });
  }
  if (listEmplacements?.[0]?.timeZone) {
    return getTimezoneName(listEmplacements?.[0]?.timeZone);
  } else {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
};

export const getAllTimezoneByEmployeId = async (employeId) => {
  const valueEmplacement = await EmployerEmplacement.getByEmployer(employeId);
  let listEmplacements = valueEmplacement?.data?.data?.[0]?.Emplacement;
  if (listEmplacements) {
    listEmplacements.sort((emplacement1, emplacement2) => {
      if (emplacement1._id < emplacement2._id) return -1;
      if (emplacement1._id > emplacement2._id) return 1;
      return 0;
    });
  }
  let timezones = [];
  for (let emplacement of listEmplacements) {
    timezones.push(getTimezoneName(emplacement.timeZone));
  }
  return timezones;
};

export const getMomentHourTimezone = (hour, timezone) => {
  const START = moment(hour).format("YYYY-MM-DD HH:mm");
  return moment.tz(START, timezone).format();
};

export const getEmployerFullLegalName = (employer) =>
  employer?.personnalInformation?.[0]?.legalFirstName +
  " " +
  employer?.personnalInformation?.[0]?.legalName;

export const formatDateToMoment = (date) => {
  return date && moment(date).isValid()
    ? moment.tz(date, "UTC").format("DD/MM/YYYY")
    : null;
};

export const putNoteToANewLine = (note) => {
  if (note?.length && note?.[0]) {
    return note?.split("\n")?.map((data, key) => {
      return <div key={uniqueId()}>{data}</div>;
    });
  }
};

export const removeANewLine = (note) => {
  if (note) return note?.replace(/\n/g, "");
};

export const scrollIntoView = (el) => {
  if (typeof el?.scrollIntoView === "function") {
    el.scrollIntoView();
    el.classList.add("colorFlash");
  }
};

export const isValidFile = (location, regexp = false) => {
  if (regexp) return location.match(regexp);
  else return true;
};

export const getFolderToExpend = (allNodes) => {
  let expanded = [];
  const setExpanded = (nodes) => {
    if (Array.isArray(nodes)) {
      for (const node of nodes) {
        if (node.children) {
          expanded.push(node.value);
          setExpanded(node.children);
        }
      }
    }
  };
  setExpanded(allNodes);
  return expanded;
};

export const dayHourToHour = (day, hour, hourPerDay = 24) => {
  hour = parseFloat(hour) || 0;
  day = parseFloat(day) || 0;

  day = (day * hourPerDay).toFixed(2);
  return day + hour;
};

// Checking if is a valid JSON string
export const isJsonString = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const parseJsonObject = (obj) => {
  try {
    obj = JSON.parse(obj);
    return obj;
  } catch (e) {
    return {};
  }
};

//date picker helper
function isPreviousButton(element) {
  return (
    element?.firstElementChild &&
    element?.firstElementChild?.className?.includes("years-previous")
  );
}

function isUpCummingButton(element) {
  return (
    element?.firstElementChild &&
    element?.firstElementChild?.className?.includes("years-upcoming")
  );
}

function textContent(element) {
  return +element?.innerText?.replace("✓\n", "") || 0;
}

function sortYear(a, b) {
  if (isPreviousButton(a) || isUpCummingButton(b)) {
    return -1;
  }
  if (isUpCummingButton(a) || isPreviousButton(b)) {
    return 1;
  }

  return textContent(a) - textContent(b);
}

export function reverseDropwdownYear() {
  setTimeout(() => {
    const yearDropdown = this.firstElementChild;
    if (yearDropdown?.childNodes?.length) {
      let childElements = Array.from(yearDropdown.childNodes);
      childElements.sort(sortYear);
      yearDropdown.append(...childElements);
    }

    // scroll vers l'année selectionnée
    const datePickerDropdown = document.querySelector(
      ".react-datepicker__year-dropdown"
    );
    if (datePickerDropdown) {
      const selectedYear = document.querySelector(
        ".react-datepicker__year-option--selected_year"
      );
      // @ts-ignore
      if (selectedYear && selectedYear.offsetTop) {
        // @ts-ignore
        datePickerDropdown.scroll(0, selectedYear.offsetTop - 5);
      }
    }
  }, 0); //<- permet d'attendre que le dropdown s'affiche
}

// @deprecated
export function getGroupName(data = []) {
  let result = data;
  for (let value of data) {
    let groupeName = [];
    for (let res of value?.groupe) {
      if (!groupeName.includes(res?.Groupe?.[0]?.title)) {
        groupeName.push(res?.Groupe?.[0]?.title);
      }
    }
    value.groupeName = groupeName?.length ? groupeName.join(", ") : "Aucun";
    if (value?.emplacement) {
      let emplacementName = [];
      for (let res of value?.emplacement) {
        emplacementName.push(res?.id?.name);
      }
      value.emplacementName = emplacementName?.length
        ? emplacementName.join(", ")
        : "Aucun";
    }
  }
  return result;
}

// //Replace {first_name}, {last_name} and {job_title} in email content
// export const replaceAllContentVars = (content, candidate) => {
//   if (typeof content === "string" && candidate) {
//     content = content
//       .replaceAll("{applicant_firstname}", candidate.firstname)
//       .replaceAll("{applicant_lastname}", candidate.name)
//       .replaceAll("{job_title}", candidate.job?.title);
//   }
//   return content;
// };
const getTokenSafeString = (value) => {
  if (value === undefined || value === null) return "";
  return String(value);
};

const getCandidateJobTitle = (candidate, fallbackJobTitle = "") => {
  const candidateJob = candidate?.job;

  if (candidateJob && typeof candidateJob === "object") {
    if (candidateJob.title) return candidateJob.title;
    if (candidateJob.name) return candidateJob.name;
  }

  if (candidate?.jobTitle) return candidate.jobTitle;
  if (candidate?.job_title) return candidate.job_title;

  return fallbackJobTitle;
};

// Replace {first_name}, {last_name} and {job_title} in email content
export const replaceAllContentVars = (content, candidate, options = {}) => {
  if (typeof content === "string" && candidate) {
    const jobTitle = getCandidateJobTitle(candidate, options.jobTitle);

    content = content
      .replaceAll("{applicant_firstname}", getTokenSafeString(candidate.firstname))
      .replaceAll("{applicant_lastname}", getTokenSafeString(candidate.name))
      .replaceAll("{job_title}", getTokenSafeString(jobTitle));
  }
  return content;
};

export const monthDiff = (d1, d2) => {
  var months;
  months = (d2.getFullYear() - d1.getFullYear()) * 12;
  months -= d1.getMonth();
  months += d2.getMonth();
  return months <= 0 ? 0 : months;
};

//add enterprise name before email from params
export const setParamsEmailFrom = (email) => {
  const enterpriseName = JSON.parse(
    localStorage.getItem("Enterprise")
  )?.enterpriseName;
  if (email && enterpriseName) {
    email = enterpriseName + " <" + email + ">";
  }
  return email;
};

//get candidate full name
export const getCandidateFullName = (candidate) =>
  (candidate?.firstname ? candidate.firstname + " " : "") +
  (candidate?.name ? candidate.name : "");

//parse a string to array (customized JSON array parse)
export const forceParseToArray = (s) =>
  !s
    ? []
    : s.indexOf("[") === 0 && s.lastIndexOf("]") >= s.length - 1
      ? JSON.parse(s)
      : Array.isArray(s)
        ? s
        : [s];

//unwind an array of object by one property
// ex: arr = [ {a: 1, b:[2, 3] } ] => unwindArray(arr, "b") = [ {a: 1, b: 2 }, {a: 1, b: 3 } ]
export const unwindArray = (arr, prop) => {
  if (Array.isArray(arr) && typeof prop === "string") {
    arr = arr.reduce((acc, item) => {
      let unwindedItems = [item];
      if (Array.isArray(item[prop])) {
        unwindedItems = item[prop].map((p) => ({ ...item, [prop]: p }));
      }
      return acc.concat(unwindedItems);
    }, []);
  }
  return arr;
};

export const cleanObject = (obj) => {
  const cleaned = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined && value !== "") {
      cleaned[key] = value;
    }
  }

  return cleaned;
};

// deep clone value of one variable or constant
export const cloneValue = (val) => JSON.parse(JSON.stringify(val));

export const absoluteUrl = (url) =>
  (/^(http(s)?:\/\/){1}/.test(url) ? "" : "http://") + url;

export const getRGBA = (r, g, b, a) => {
  return "rgba(" + r + "," + g + "," + b + "," + a + ")";
};

export function setTimezoneHours(hours, timezone) {
  let newHours = [...hours];
  let timezoneValue = null;
  if (Boolean(timezone)) {
    timezoneValue = getTimezoneName(timezone);
  }
  newHours = newHours.map((hour) => {
    if (!timezone) timezoneValue = hour.Timezone || hour.timezone;
    hour.start = getDateToRender(hour.startDate, timezoneValue);
    hour.end = getDateToRender(hour.endDate, timezoneValue);
    if (moment(hour.end).format("HHmm") === "0000") {
      hour.end = moment(hour.end).add(-1, "minutes").toDate();
    }
    return hour;
  });
  return newHours;
}

//for recrutement
export const convertURLToFile = async (url, data) => {
  let blob = await fetch(url).then((r) => r.blob());
  let file = new File([blob], data.filename, { type: data.mimetype });
  return file;
};

//calculate hours remain (sick, miss, vacancy) by renewal date and hiring date
// **** used when employer set vacancies hours but not set the current hours
export const calculateHoursRemaining = (
  renewalDay,
  hiringDate,
  hoursPerYears
) => {
  let yearsDiff = null,
    hoursRemain = "";
  renewalDay = moment(renewalDay).set("hour", 0);
  hiringDate = moment(hiringDate).set("hour", 0);
  hoursPerYears = parseFloat(hoursPerYears);
  if (renewalDay <= hiringDate || renewalDay <= moment()) {
    yearsDiff = moment(renewalDay.clone().add(1, "years")).diff(
      hiringDate,
      "years",
      true
    );
  } else {
    yearsDiff = moment(renewalDay).diff(hiringDate, "years", true);
  }
  if (yearsDiff < 1) {
    let monthDiff = null;
    if (renewalDay <= hiringDate) {
      monthDiff = moment(renewalDay.clone().add(1, "years")).diff(
        hiringDate,
        "months",
        true
      );
    } else {
      monthDiff = moment(renewalDay).diff(hiringDate, "months", true);
    }
    hoursRemain = isNaN(hoursPerYears)
      ? ""
      : ((hoursPerYears * monthDiff) / 12).toFixed(2);
  } else {
    hoursRemain = hoursPerYears;
  }
  return hoursRemain;
};

export const checkHoursHolidaysRemaining = (employeSalary = {}) => {
  const holiday = employeSalary.holiday;
  let holidayRemaining = employeSalary.holidayRemaining;
  const hiringDate = employeSalary.job?.hiringDate;
  // if (holiday?.hoursIllimited && holidayRemaining?.hoursHoliday === "") {
  //   holidayRemaining.hoursIllimited = true;
  // } else if (
  //   !holiday?.hoursIllimited &&
  //   !holidayRemaining?.hoursIllimited &&
  if (holiday?.hoursHoliday !== "" && holidayRemaining?.hoursHoliday === "") {
    holidayRemaining.hoursHoliday = calculateHoursRemaining(
      holiday.renewalDay,
      hiringDate,
      holiday.hoursHoliday
    );
  }
  if (holiday?.hoursSick !== "" && holidayRemaining?.hoursSick === "") {
    holidayRemaining.hoursSick = calculateHoursRemaining(
      holiday.renewalDay,
      hiringDate,
      holiday.hoursSick
    );
  }
  if (holiday?.hoursMiss !== "" && holidayRemaining?.hoursMiss === "") {
    holidayRemaining.hoursMiss = calculateHoursRemaining(
      holiday.renewalDay,
      hiringDate,
      holiday.hoursMiss
    );
  }
  return holidayRemaining;
};

export function formatHolidays(employerSalary = {}) {
  const holiday = employerSalary?.holiday;
  const holidayRemaining = employerSalary?.holidayRemaining;

  return {
    [VACANCE]: {
      /** @type {string} */
      total: holiday?.hoursHoliday || "",

      /** @type {string} */
      remaining: holidayRemaining?.hoursHoliday || "",

      /** @type {boolean} */
      hoursIllimited: /*holidayRemaining?.hoursIllimited*/ false
    },
    [ABSENCE]: {
      /** @type {string} */
      total: holiday?.hoursMiss || "",

      /** @type {string} */
      remaining: holidayRemaining?.hoursMiss || ""
    },
    [MALADIE]: {
      /** @type {string} */
      total: holiday?.hoursSick || "",

      /** @type {string} */
      remaining: holidayRemaining?.hoursSick || ""
    },
    /** @type {string} */
    hoursPerDay: holiday?.hoursPerDay || ""
  };
}

export function getDateFormat(startDate, endDate, language) {
  let start = moment(startDate).format("dddd D MMMM YYYY");
  let end = moment(endDate).format("dddd D MMMM YYYY");
  if (start === end) {
    return start;
  } else {
    end = moment(endDate).format("D MMMM YYYY");
    if (
      moment(startDate).format("MMMM YYYY") ===
      moment(endDate).format("MMMM YYYY")
    ) {
      start = moment(startDate).format("D");
    } else if (
      moment(startDate).format("YYYY") === moment(endDate).format("YYYY")
    ) {
      start = moment(startDate).format("D MMMM");
    } else {
      start = moment(startDate).format("D MMMM YYYY");
    }

    if (language === "fr") {
      return start + " au " + end;
    } else if (language === "en") {
      return start + " to " + end;
    }
  }
}

export const daysAndHoursLeave = (startDate, endDate, nbHoursPerDay) => {
  let nbMinutes = moment(endDate).diff(startDate, "minutes");
  let nbHours = Math.floor(nbMinutes / 60);
  let manualDayLeave = Math.floor(nbHours / 24);
  let manualHoursLeave = nbHours % 24;
  nbMinutes = nbMinutes % 60;
  manualHoursLeave += Math.round((nbMinutes / 60) * 100) / 100;
  if (manualHoursLeave >= nbHoursPerDay) {
    manualDayLeave++;
    manualHoursLeave = 0;
  }
  if (manualDayLeave < 0) manualDayLeave = 0;
  if (manualHoursLeave < 0) manualHoursLeave = 0;
  return { manualDayLeave, manualHoursLeave };
};

export const getSelectedAssignments = (
  assignations,
  selectedAssignment,
  elements = {},
  level = 0,
  titles = []
) => {
  if (assignations?.length) {
    const nextLevel = level + 1;
    for (const assignation of assignations) {
      const newElements = Object.assign({}, elements, {
        ["level" + level]: assignation
      });
      const newTitles = [...titles, assignation?.title];
      if (assignation._id === selectedAssignment) {
        return {
          selectedAssignement: newElements,
          titles: newTitles?.join(" / ")
        };
      } else if (assignation.childElements?.length) {
        elements = getSelectedAssignments(
          assignation.childElements,
          selectedAssignment,
          newElements,
          nextLevel,
          newTitles
        );
      }
    }
  }
  return elements;
};

export function diacriticSensitiveRegex(string = "") {
  return string
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    .replaceAll(/ae/g, "[ae,æ]")
    .replaceAll(/oe|œ/g, "[oe,œ]")
    .replaceAll(/AE|Æ/g, "[AE,Æ]")
    .replaceAll(/OE|Œ/g, "[OE,Œ]")
    .replaceAll(/a|á|à|â|ä|å|ã/g, "[a,á,à,â,ä,å,ã]")
    .replaceAll(/c|ç/g, "[c,ç]")
    .replaceAll(/e|é|è|ê|ë/g, "[e,é,è,ê,ë]")
    .replaceAll(/i|í|ì|î|ï/g, "[i,í,ì,î,ï]")
    .replaceAll(/n|ñ/g, "[n,ñ]")
    .replaceAll(/o|ó|ò|ô|ö|õ|ø/g, "[o,ó,ò,ô,ö,õ,ø]")
    .replaceAll(/s|š/g, "[s,š]")
    .replaceAll(/u|ú|ù|û|ü/g, "[u,ú,ù,û,ü]")
    .replaceAll(/y|ý|ÿ/g, "[y,ý,ÿ]")
    .replaceAll(/z|ž/g, "[z,ž]")
    .replaceAll(/A|Á|À|Â|Ä|Å|Ã/g, "[A,Á,À,Â,Ä,Å,Ã]")
    .replaceAll(/C|Ç/g, "[C,Ç]")
    .replaceAll(/E|É|È|Ê|Ë/g, "[E,É,È,Ê,Ë]")
    .replaceAll(/I|Í|Ì|Î|Ï/g, "[I,Í,Ì,Î,Ï]")
    .replaceAll(/N|Ñ/g, "[N,Ñ]")
    .replaceAll(/O|Ó|Ò|Ô|Ö|Õ|Ø/g, "[O,Ó,Ò,Ô,Ö,Õ,Ø]")
    .replaceAll(/S|Š/g, "[S,Š]")
    .replaceAll(/U|Ú|Ù|Û|Ü/g, "[U,Ú,Ù,Û,Ü]")
    .replaceAll(/Y|Ý|Ÿ/g, "[Y,Ý,Ÿ]")
    .replaceAll(/Z|Ž/g, "[Z,Ž]")
    .replaceAll(/'|`/g, "[',`]");
  /*  .replaceAll(")", "[)]")
    .replaceAll("(", "[(]")
    .replaceAll("+", "[+]")
    .replaceAll("*", "[*]") */
}

export const getRegSearchValue = (name) => {
  let nameReg = null;
  if (name) {
    // Échapper les caractères spéciaux des expressions régulières
    const escapeRegExp = (string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    };

    // Extraction des propriétés des filtres
    const normalized = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const nameParts = normalized.split(/\s+/);

    // Construction de l'expression régulière pour chaque partie du nom
    const nameRegexParts = nameParts.map(
      (part) => `(?=.*${escapeRegExp(part)})`
    );
    nameReg = new RegExp(nameRegexParts.join(""), "i");
  }
  return nameReg;
};

export const replaceSpecialCharacter = (str) => {
  if (!str) return "";
  return str.replace(/[^\w\s]/gi, "");
};

export const searchValue = (nameReg, source) => {
  return nameReg.test(source.normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
};

export const optimizedSearch = (inputSearch, source) => {
  const nameReg = getRegSearchValue(inputSearch);
  return nameReg ? searchValue(nameReg, source) : false;
};

export const arraySearch = (value, array, attribut) => {
  if (!value) return array;
  const regSearchValue = getRegSearchValue(value);
  const newArray = array.filter((item) =>
    searchValue(regSearchValue, item[attribut])
  );
  return newArray;
};

export const taskIndentationStyle = (indentation = 2) => {
  return { marginLeft: "" + indentation + "rem" };
};

export const convertTaskHours = (
  hoursOrMinutes,
  minVersion = false,
  v2 = false
) => {
  let minutes = isNaN(hoursOrMinutes) ? 0 : hoursOrMinutes;
  minutes = minVersion ? Math.round(minutes) : Math.round(minutes * 60);
  const m = minutes % 60;
  const h = Math.floor(minutes / 60);
  if (!v2) {
    return (h === 0 ? "0" : twoDigit(h)) + "h" + (m === 0 ? "" : twoDigit(m));
  } else {
    return twoDigit(h) + "h" + twoDigit(m);
  }
};

export const convertHourDuration = (hours) => {
  let h = 0,
    m = 0;
  if (hours) {
    h = parseInt(hours);
    if (Number.isNaN(h)) {
      h = 0;
    } else {
      m = (parseFloat(hours) * 60) % 60;
    }
  }
  return (
    "" + (h > 0 ? h + "h" : "") + (m > 0 ? twoDigit(m.toFixed(0)) + "mn" : "")
  );
};

export const convertStringToHours = (str) => {
  let time = str.split("h");
  return +time[0] + +time[1] / 60;
};

/**
 * @param { Number } hourDuration
 * @param { String | null } separatedChar
 * @returns { String }
 */
export const convertHourDurationToHourAndMinute = (
  hourDuration,
  separatedChar = ":"
) => {
  if (!hourDuration) return "";
  const hours = Math.floor(hourDuration),
    minutes = (hourDuration * 60) % 60;
  return `${twoDigit(hours)}${separatedChar}${twoDigit(minutes?.toFixed(0))}`;
};

export const convertMinutesDurationToHourAndMinute = (
  minutesDuration,
  separatedChar = ":"
) => {
  if (!minutesDuration) return "";
  const hours = Math.floor(minutesDuration / 60),
    minutes = minutesDuration % 60;
  return `${twoDigit(hours)}${separatedChar}${twoDigit(minutes?.toFixed(0))}`;
};

/**
 * @returns {Date | null}
 */
export const getDateToRender = (date, timezone = "UTC") => {
  if (!date || !moment(date).isValid()) {
    return null;
  }

  const formattedDate = moment.tz(date, timezone).format("YYYY-MM-DD HH:mm:ss");
  return moment(formattedDate).startOf("minute").toDate();
};

export const getDateToSave = (date, timezone = "UTC") => {
  let dateToSave = null;
  if (date && moment(date).isValid())
    dateToSave = moment
      .tz(moment(date).format("YYYY-MM-DD HH:mm:ss"), timezone)
      .toDate();
  return dateToSave;
};

export const calculateTimes = (startTimes, endTimes) => {
  if (!startTimes || !endTimes)
    return {
      hours: 0,
      minutes: 0
    };
  const durations = moment(endTimes).diff(startTimes, "minutes", false);
  return {
    hours: Math.floor(durations / 60),
    minutes: parseInt((durations % 60).toFixed(0)),
    durations
  };
};

export const flatEmployersTree = (employers) =>
  employers.reduce((acc, employer) => {
    let childs = [];
    if (employer.childElements.length) {
      childs = flatEmployersTree(employer.childElements);
    }
    return [...acc, employer._id, ...childs];
  }, []);

export const filterEmployeeDropDownByPermission = (
  employers,
  permissions = []
) => {
  if (!Array.isArray(employers)) return [];
  return employers.filter(
    (e) =>
      Array.isArray(e?.permissions) &&
      e.permissions.some((p) => permissions.includes(p))
  );
};

export const hasReachedBottom = (viewElement, toleranceMargin = 0) => {
  if (typeof viewElement === "object" && viewElement instanceof HTMLElement) {
    // version amélioré de:
    // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#determine_if_an_element_has_been_totally_scrolled
    if (
      viewElement.scrollHeight - Math.abs(viewElement.scrollTop) <=
      viewElement.clientHeight + toleranceMargin
    ) {
      return true;
    }
  }

  return false;
};
export const setNotificationSuccess = (dispatch, message, delay = 0) => {
  setTimeout(() => {
    dispatch({
      type: "notification/STANDARD_NOTIFICATION",
      payload: {
        type: SUCCESS_ACTION,
        message
      }
    });
  }, delay);
};
export const setNotificationWarning = (dispatch, message, delay = 0) => {
  setTimeout(() => {
    dispatch({
      type: "notification/STANDARD_NOTIFICATION",
      payload: {
        type: WARNING_ACTION,
        message
      }
    });
  }, delay);
};

export const setNotificationError = (dispatch, message, delay = 0) => {
  setTimeout(() => {
    dispatch({
      type: "notification/STANDARD_NOTIFICATION",
      payload: {
        type: ERROR,
        message
      }
    });
  }, delay);
};

export const hexToRgb = (hex) => {
  var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b;
  });

  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ]
    : null;
};
const componentToHex = (c) => {
  var hex = c.toString(16);
  return hex.length == 1 ? "0" + hex : hex;
};

export const rgbToHex = (r, g, b) => {
  return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
};
export const setNotificationInfo = (dispatch, message, delay = 0) => {
  setTimeout(() => {
    dispatch({
      type: "notification/STANDARD_NOTIFICATION",
      payload: {
        type: INFO_ACTION,
        message
      }
    });
  }, delay);
};

export const removeOptionalParams = (path) => path?.replace(/\/\:.+\?/g, "");

export const removeAllPathParams = (path) => path?.replace(/\/\:.+\??/g, "");

/* Traduction des parties de variables des notifications*/
const translateOptions = (options, translator) => {
  if (options) {
    for (const key in options) {
      options[key] = translator(`message:${options[key]}`);
    }
  }
  return options;
};
/*Function reduite pour la traduction du message et les variables des notifications */
export const translateNotification = (translator, message, option) => {
  return capitalizeSentences(
    translator(`message:${message}`, translateOptions(option, translator))
  );
};
export const translatePipelineColumnsTitle = (translator, columns) => {
  if (columns?.lanes?.[0] && typeof translator === "function") {
    columns.lanes = columns.lanes.map(({ title, ...column }) => ({
      title: translator(title),
      ...column
    }));
  }
  return columns;
};

export const getFormat = (hourValue) => {
  if (hourValue === "0") return true;
  const formats = [
    "h",
    "hA",
    "hm",
    "hmA",
    "hh",
    "hhA",
    "hhm",
    "hhmA",
    "hmm",
    "hmmA",
    "hhm",
    "hhmA",
    "hhmm",
    "hhmmA",
    "h:",
    "h:A",
    "h:m",
    "h:mA",
    "hh:",
    "hh:A",
    "hh:m",
    "hh:mA",
    "h:mm",
    "h:mmA",
    "hh:m",
    "hh:mA",
    "hh:mm",
    "hh:mmA"
  ];
  for (let format of formats) {
    if (moment(hourValue, format, true).isValid()) return format;
  }
  return false;
};

export const validateInputHourFormat = (hourValue) => {
  if (!hourValue) return true;
  hourValue = hourValue.toUpperCase();
  hourValue = hourValue.replaceAll(" ", "");
  if (!hourValue.includes("M")) {
    hourValue = hourValue.replaceAll("A", "AM").replaceAll("P", "PM");
  }
  let format = getFormat(hourValue);
  return format;
};

export const getFormatedValueHoursMin = (hourValue) => {
  if (!hourValue) return "";

  // Supprime tout sauf les chiffres
  const raw = hourValue.toString().replace(/\D/g, "").slice(0, 4); // max 4 chiffres

  if (raw.length === 0) return "";

  // Format automatique HH:mm
  let formatted = raw;
  if (raw.length >= 3) {
    formatted = raw.slice(0, 2) + ":" + raw.slice(2);
  }

  // Vérifie si c'est une heure valide (si 4 chiffres sont présents)
  if (raw.length === 4) {
    const hours = parseInt(raw.slice(0, 2), 10);
    const minutes = parseInt(raw.slice(2), 10);
    if (hours > 23 || minutes > 59) {
      return "";
    }
  }

  return formatted;
};

export function formatToNumber(eventValue) {
  return eventValue.toString().replace(REGEX_NOT_NUMBER, "");
}

export const setStateColorRGBA = (colorPicked, tempSettings) => {
  tempSettings["rgba"] = getRGBA(
    colorPicked.r,
    colorPicked.g,
    colorPicked.b,
    0.2
  );
  return tempSettings;
};

export const combineTwoArrays = (array1, array2) => {
  return array1.concat(array2.filter((item) => array1.indexOf(item) < 0));
};

export const getPostalCode = (place) => {
  const { long_name: postalCode = "" } =
    place?.address_components?.find((c) => c.types.includes("postal_code")) ||
    {};
  return postalCode;
};

export const getCountry = (place) => {
  const { long_name: country = "", short_name: countryCode = "" } =
    place?.address_components?.find((c) => c.types.includes("country")) || {};
  return { country, countryCode };
};

export const getProvince = (place) => {
  const { long_name: province = "" } =
    place?.address_components?.find((c) =>
      c.types.includes("administrative_area_level_1")
    ) || {};
  return province;
};

export const getProvinceAndCode = (place) => {
  const { long_name: province = "", short_name: provinceCode = "" } =
    place?.address_components?.find((c) =>
      c.types.includes("administrative_area_level_1")
    ) || {};
  return { province, provinceCode };
};

export const getLocality = (place) => {
  const { long_name: city = "" } =
    place?.address_components?.find((c) => c.types.includes("locality")) || {};
  return city;
};

export const getStreetAndNumber = (place) => {
  const { long_name: street = "" } =
    place?.address_components?.find((c) => c.types.includes("route")) || {};
  const { long_name: streetNumber = "" } =
    place?.address_components?.find((c) => c.types.includes("street_number")) ||
    {};
  return { street, streetNumber };
};

export const getCurrencyByDevise = (devise) =>
  devise ? `${devise.pays}, ${devise.monnaie} (${devise.devise})` : "";
export const toString = (mix) => (mix ? mix.toString() : "");

export const detecterDevise = (chaine) => {
  const match = chaine.match(/\(([^)]+)\)/);
  return match ? match[1] : null;
};

export const isOptionalPhoneValid = (phonenumber) =>
  !phonenumber || REGEX_PHONE.test(phonenumber);

export const isOptionalEmailValid = (email) =>
  !email || REGEX_EMAIL.test(email);

export const getDateUtcZero = (date, format = "DD/MM/YYYY") => {
  if (date && moment(date).isValid()) {
    date = moment(moment(date).format(format), format).utcOffset(0).toDate();
  }
  return date;
};

export const convertBytesAndShow = (bytes, decimals = 2) => {
  if (bytes <= 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "Kb", "Mb", "Gb", "Tb"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const showDeviseInEn = (number) => {
  if (!number || isNaN(parseFloat(number))) {
    number = 0;
  }
  return parseFloat(parseFloat(number).toFixed(0)).toLocaleString("en");
};

export const showDeviseInEnFixed2 = (number) => {
  if (!number || isNaN(parseFloat(number))) {
    number = 0;
  }

  const parsed = parseFloat(number);

  return parsed.toLocaleString("en", {
    minimumFractionDigits: parsed % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  });
};

export function removeA(arr) {
  var what,
    a = arguments,
    L = a.length,
    ax;
  while (L > 1 && arr.length) {
    what = a[--L];
    while ((ax = arr.indexOf(what)) !== -1) {
      arr.splice(ax, 1);
    }
  }
  return arr;
}

export const upperCaseFirstLetter = (a) =>
  (a + "").charAt(0).toUpperCase() + a.substr(1);

export const formatDurationToNow = (date, locale) => {
  let lastModified = "";
  if (moment(date).isValid()) {
    lastModified = formatDistanceToNow(moment(date).toDate(), {
      addSuffix: true,
      locale: locales[locale]
    });
    lastModified = upperCaseFirstLetter(lastModified);
  }
  return lastModified;
};

export const parseEmojis = (value) => {
  if (value) {
    const emojisArray = toArray(value);
    // toArray outputs React elements for emojis and strings for other
    const newValue = emojisArray.reduce((previous, current) => {
      if (typeof current === "string") {
        return previous + current;
      }
      // @ts-ignore
      return previous + current?.props?.children;
    }, "");
    return newValue;
  }
};

export function formatDateToDateString(date) {
  if (!date || !moment(date).isValid()) {
    return "";
  }

  return moment(date).format("YYYY-MM-DD");
}

export function formatDateStringToDate(date) {
  const dateMoment = moment(date, "YYYY-MM-DD");
  if (!dateMoment.isValid()) {
    return null;
  }

  return dateMoment.toDate();
}

export function convertToFormData(args) {
  const formData = new FormData();

  // in multipart/form-data, "files" attribut should be at the end or object
  let { files = [], ...formValue } = args;
  formValue.files = files;

  for (let key in formValue) {
    if (Array.isArray(formValue[key])) {
      for (let subElement of formValue[key]) {
        if (typeof subElement !== "undefined") formData.append(key, subElement);
      }
      continue;
    }

    if (typeof formValue[key] !== "undefined")
      formData.append(key, formValue[key]);
  }

  return formData;
}

export function formatageDate(str) {
  return moment(str).isValid() ? moment(str).format("DD/MM/YYYY") : "-";
}

// Compression d'image / fichier

// Other
export const compressedImage = async (imageFile, customOptions = {}) => {
  const options = {
    maxSizeMB: 0.2,
    maxWidthOrHeight: 500,
    useWebWorker: true,
    initialQuality: 1,
    fileType: imageFile.type,
    ...customOptions
  };
  try {
    const Blob = await imageCompression(imageFile, options);
    const compressedFile = new File([Blob], imageFile.name, {
      type: imageFile.type
    });
    return compressedFile;
  } catch (error) {
    console.log(error);
  }
};

// Nouvelle
export const compressedImageNews = async (imageFile, customOptions = {}) => {
  const img = new Image();
  const objectURL = URL.createObjectURL(imageFile);

  img.src = objectURL;

  return new Promise((resolve, reject) => {
    img.onload = async () => {
      const { width } = img;

      // Si la largeur est déjà en dessous de 1920px = no compress
      if (width <= 1920) {
        resolve(imageFile);
        return;
      }

      // Param
      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        initialQuality: 0.85,
        fileType: imageFile.type,
        ...customOptions
      };

      try {
        const compressedBlob = await imageCompression(imageFile, options);

        const compressedFile = new File([compressedBlob], imageFile.name, {
          type: imageFile.type
        });

        resolve(compressedFile);
      } catch (error) {
        console.error("Compression failed:", error);
        reject(error);
      } finally {
        // publie URL
        URL.revokeObjectURL(objectURL);
      }
    };

    img.onerror = (err) => {
      reject(new Error("Failed to load image"));
    };
  });
};
// END Compression d'image / fichier

export const getFileType = (data) => {
  let file = [];
  if (data?.type) {
    file = data.type.split("/");
  } else if (typeof data === "string") {
    file = data.split("/");
  }
  return file[0];
};

export const getMimeType = (data) => {
  let mime = [];
  if (data?.type) {
    mime = data.type.split("/");
  } else if (typeof data === "string") {
    mime = data.split("/");
  }
  return mime[1];
};

export const dataURLtoFile = (dataurl, filename) => {
  var arr = dataurl?.split(",");
  if (arr?.[0]) {
    let mime = arr[0].match(/:(.*?);/)[1];
    let bstr = atob(arr[1]);
    let n = bstr.length;
    let u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }
};

export const getPermissionValue = (permissionLists, permissionValues = {}) => {
  for (const [key, data] of Object.entries(permissionLists)) {
    permissionValues[key] = data.children?.length
      ? data.children.reduce(
        (permissions, { value, childs, children = [] }) => {
          if (childs) {
            permissionValues = getPermissionValue(childs, permissionValues);
          }
          if (children?.length) {
            permissions.push(
              ...children
                .map((childNode) => getNodeValues({ ...childNode })?.flat())
                .flat()
            );
          }
          permissions.push(value);
          return permissions;
        },
        []
      )
      : [data.value];
    if (data.childs) {
      permissionValues = getPermissionValue(data.childs, permissionValues);
    }
  }
  return permissionValues;
};

export const getAllPermissionValue = (permissionLists) =>
  Object.values(getPermissionValue(permissionLists)).flat();

export const getTabValue = (permissionLists, permissionValues = {}) => {
  for (const [key, data] of Object.entries(permissionLists)) {
    permissionValues[key] = data.children?.length
      ? data.children.map(({ value, childs, defaultActive, label, isBeta }) => {
        if (childs) {
          permissionValues = getTabValue(childs, permissionValues);
        }
        return {
          Tabs: value,
          defaultActive,
          label,
          isBeta
        };
      })
      : [
        {
          Tabs: data.value,
          defaultActive: data.defaultActive,
          label: data.label,
          isBeta: data.isBeta
        }
      ];
    if (data.childs) {
      permissionValues = getTabValue(data.childs, permissionValues);
    }
  }
  return permissionValues;
};

export const getSections = (permissionLists) =>
  Object.values(getPermissionValue(permissionLists))
    .flat()
    ?.filter((value) => value?.includes("Section"));

const getNodeValues = (node) => {
  let nodeValues = [node.value];
  if (node.childs) {
    // const _values = Object.values(node.childs).map((childNode) => getNodeValues({ ...childNode })).flat(2);
    const _values = Object.values(node.childs).reduce(
      (childsValues, childNode) => {
        const childValues = getNodeValues({ ...childNode });
        childsValues.push(...childValues);
        if (childValues.length > 1) childsValues.push(childValues.join(","));
        return childsValues;
      },
      []
    );
    nodeValues.push(..._values);
    // if (_values.length > 1) nodeValues.push(_values.join(","));
  }
  if (node?.children?.length) {
    const _values = node.children.reduce((childsValues, childNode) => {
      const childValues = getNodeValues({ ...childNode });
      childsValues.push(...childValues);
      if (childValues.length > 1) childsValues.push(childValues.join(","));
      return childsValues;
    }, []);
    nodeValues.push(..._values);
    // if (_values.length > 1) nodeValues.push(_values.join(","));
  }
  // nodeValues.push(nodeValues.join(","));
  return nodeValues;
};

const getChildsPermissionTree = (childsNodes, parentValues = []) =>
  childsNodes.map((childNode) => {
    const value = getNodeValues({ ...childNode });
    const updatedParentValues = [...parentValues, childNode.value];
    if (childNode.children) {
      childNode.children = getChildsPermissionTree(
        [...childNode.children],
        updatedParentValues
      );
    }
    if (childNode.childs) {
      if (!childNode.children) childNode.children = [];
      childNode.children = childNode.children.concat(
        ...Object.values(childNode.childs).map((subChild) =>
          //getChildsPermissionTree({ ...subChild }.children)
          getChildsPermissionTree([subChild], updatedParentValues)
        )
      );
    }
    return {
      ...childNode,
      value: value.join(","),
      values: value,
      parentValues: [...parentValues, childNode.value],
      key: childNode.value
    };
  });

export const getPermissionTree = (permissionLists) => {
  let permissionTree = Object.entries(permissionLists).map(([key, data]) => {
    let value = [data.value];
    const updatedParentValues = [data.value];
    if (data.children?.length) {
      value = getNodeValues(data);
      data.children = getChildsPermissionTree(
        [...data.children],
        updatedParentValues
      );
    }
    return {
      ...data,
      value: value.join(","),
      values: value,
      key: data.value,
      parentValues: updatedParentValues
    };
  });
  return permissionTree;
};

// Get the difference between two array, return an array
export const arrayDifference = (array1, array2, key = "_id") => {
  return array1.filter((object1) => {
    return !array2.some((object2) => {
      return object1[key] === object2[key];
    });
  });
};

// Compare two array of object and return true or false
export const compareArraysOfObject = (
  array1 = [],
  array2 = [],
  key = "_id"
) => {
  if (!Array.isArray(array1) && !Array.isArray(array2)) {
    return false;
  }
  const arrayDiff1 = arrayDifference(array1, array2, key);
  const arrayDiff2 = arrayDifference(array2, array1, key);
  return arrayDiff1.length === 0 && arrayDiff2.length === 0;
};

//set some object properties and create new one
export const combineObjectAndClone = (obj1 = {}, obj2 = {}) =>
  Object.assign({}, obj1, obj2);

//set some object properties but not create new one
export const combineObjectWithoutCloning = (obj1 = {}, obj2 = {}) => ({
  ...obj1,
  ...obj2
});

export const downloadBlobFile = (data, filename) => {
  var blob = new Blob([data], { type: "application/octet-stream" });
  // @ts-ignore
  if (typeof window.navigator.msSaveBlob !== "undefined") {
    // IE workaround for "HTML7007: One or more blob URLs were
    // revoked by closing the blob for which they were created.
    // These URLs will no longer resolve as the data backing
    // the URL has been freed."
    // @ts-ignore
    window.navigator.msSaveBlob(blob, filename);
  } else {
    var blobURL =
      window.URL && window.URL.createObjectURL
        ? window.URL.createObjectURL(blob)
        : window.webkitURL.createObjectURL(blob);
    var tempLink = document.createElement("a");
    tempLink.style.display = "none";
    tempLink.href = blobURL;
    tempLink.setAttribute("download", filename);
    tempLink.setAttribute("data-bcup-haslogintext", "no");

    // Safari thinks _blank anchor are pop ups. We only want to set _blank
    // target if the browser does not support the HTML5 download attribute.
    // This allows you to download files in desktop safari if pop up blocking
    // is enabled.

    //tempLink.setAttribute('target', '_blank');
    document.body.appendChild(tempLink);
    tempLink.click();

    // Fixes "webkit blob resource error 1"
    setTimeout(function () {
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(blobURL);
    }, 200);
  }
};

export const getLinkInJSON = (node) => {
  if (node.type === "link" && node.url.match(REGEX_CHECK_LINKS)) {
    return node.url;
  }
  let link = "";
  if (Array.isArray(node.children)) {
    for (let deepChild of node.children) {
      const currentlink = getLinkInJSON(deepChild);
      if (currentlink) {
        link = currentlink;
        break;
      }
    }
  }
  return link;
};

export const getLink = (textToCheck) => {
  if (isJsonString(textToCheck)) {
    let parsedJNotes = JSON.parse(textToCheck);
    let children = parsedJNotes?.root?.children || [];
    for (let child of children) {
      if (Array.isArray(child.children)) {
        for (let deepChild of child.children) {
          if (
            deepChild.type === "link" &&
            REGEX_CHECK_LINKS.test(deepChild.url)
          ) {
            return deepChild.url;
          }
        }
      }
    }
  } else if (REGEX_CHECK_LINKS.test(textToCheck)) {
    return textToCheck.match(REGEX_CHECK_LINKS)?.[0];
  }
  return "";
};

export const getAllCandidatesInJobPipeline = (pipeline) => {
  let cards = [],
    columns = pipeline?.columns;
  if (typeof columns === "string") {
    columns = JSON.parse(columns);
  }
  const lanes = columns?.lanes;
  if (Array.isArray(lanes)) {
    cards = lanes
      .map((lane) =>
        lane.cards.map((card) => ({
          ...card,
          fullName: getCandidateFullName(card)
        }))
      )
      .flat();
  }
  return cards;
};

export const convertToNumericString = (value = "") => {
  const IS_COMMA_REGEX = /\,/g;
  let numericString = value.replace(IS_COMMA_REGEX, ".");

  numericString = numericString.replace(REGEX_NOT_NUMBER, "");

  const IS_DOT_REGEX = /\./g;
  if (numericString.match(IS_DOT_REGEX)?.length > 1) {
    return parseFloat(numericString).toString();
  }

  return numericString;
};

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "Kb", "Mb"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

// export const mapEmplacementToDevise = (emplacement) => {
//   return {
//     _id: emplacement?.currency?.substr(-4, 3),
//     name: emplacement?.currency?.substr(-4, 3)
//   };
// };
export const mapEmplacementToDevise = (emplacementsList) => [
  ...new Set(
    [
      ...new Set(
        emplacementsList.map((emplacement) =>
          emplacement?.currency?.substr(-4, 3)
        )
      )
    ]
      ?.sort()
      ?.map((empl) => ({ _id: empl, name: empl }))
  )
];

export const getStatusColor = (status) =>
  statusColors[status] || "arh-bg-[#FFDF6B]";

export const isFileLocationExist = async (
  file = {},
  onResponse = (args) => { }
) => {
  try {
    const isBlobUrl = file.location?.startsWith("blob:");

    if (isBlobUrl) {
      const response = await fetch(file.location);
      const isSuccess = response.ok;
      onResponse({ success: isSuccess, file, status: response.status });
    } else {
      const url = `${file.location}?cache_buster=${new Date().getTime()}`; // Éviter le cache
      const response = await fetch(url, { method: "GET", cache: "no-store" });

      const isSuccess = response.ok; // response.ok est vrai pour les statuts 200-299
      onResponse({ success: isSuccess, file, status: response.status });
    }
  } catch (err) {
    onResponse({ success: false, file });
  }
};

export const $isEmptyTextNode = (textNode) => {
  const dataNode = parseJsonObject(textNode);
  const elNode = dataNode?.root?.children?.[0]?.children?.[0];
  if (elNode && typeof elNode?.text === "string" && !elNode.text?.trim()) {
    return true;
  }
  if (
    dataNode?.root?.children?.length === 1 &&
    dataNode.root.children[0]?.children?.length === 0
  ) {
    return true;
  }
  return false;
};

const getAllTextsInNode = (node) => {
  let alltexts = "";
  if (node.text?.length) alltexts += node.text;
  if (node.question?.length) alltexts += node.question;
  if (Array.isArray(node.children)) {
    alltexts += node.children
      .map((nodeChild) => getAllTextsInNode(nodeChild))
      .join(",");
  }
  if (Array.isArray(node.options)) {
    alltexts += node.options
      .map((opition) => getAllTextsInNode(opition))
      .join(",");
  }
  return alltexts;
};

export const $getTextFromNode = (textNode) => {
  const dataNode = parseJsonObject(textNode);
  const rootNode = dataNode?.root;
  return rootNode
    ? getAllTextsInNode(rootNode)
    : typeof textNode === "string"
      ? textNode
      : "";
};

export const _getLastText = (str = "") => str.substr(str.lastIndexOf(" ") + 1);

export const _getAmountPerDistance = (distance, slices = []) => {
  if (!distance) return 0;
  let total = 0;
  let index = slices.findIndex(
    (_slice) => distance <= _slice.arrival && distance >= _slice.departure
  );
  if (index === -1) index = slices.length - 1;
  total =
    Math.max(distance - slices[index].departure + 1, 0) * slices[index].amount;
  for (let k = index - 1; k >= 0; k--) {
    total += (slices[k].arrival - slices[k].departure + 1) * slices[k].amount;
  }
  return total.toFixed(2);
};

export const isImageOrVideoFile = (file) =>
  file?.mimetype?.match(/image|video/);

export const isThereImageOrVideo = (files = []) =>
  files.some(isImageOrVideoFile);

export const getImagesAndVideos = (files = []) =>
  files.filter(isImageOrVideoFile);

export function getWindowSize() {
  const { innerWidth, innerHeight } = window;
  return { innerWidth, innerHeight };
}

export const getCityName = (autocomplete) => {
  return autocomplete?.city || autocomplete?.province || autocomplete?.country;
};

export const isSamePeriod = (periodA, periodB) =>
  moment(periodA?.startDate).isSame(periodB?.startDate, "days") &&
  moment(periodA?.endDate).isSame(periodB?.endDate, "days");

export const getInitPeriodFilterMonthe = () => {
  return {
    startDate: moment().subtract(3, "months").startOf("month").toDate(),
    endDate: moment().add(3, "months").endOf("month").toDate()
  };
};

export const getInitPeriodFilter = (months = 3) => ({
  startDate: moment().startOf("day").toDate(),
  endDate: moment().add(months, "M").endOf("day").toDate()
});

export const formatValidDate = (date, format = "DD/MM/YYYY") => {
  const dateMoment = moment(getDateToRender(date));

  if (dateMoment.isValid()) {
    return dateMoment.format(format);
  }

  return "-";
};
export const getCurrentMonthPeriod = () => ({
  startDate: moment().startOf("month").toDate(),
  endDate: moment().endOf("month").toDate()
});

export const getLastThreeMonth = () => ({
  startDate: moment().subtract(3, "month").startOf("month").toDate(),
  endDate: moment().endOf("month").toDate()
});

export const objToNamedList = (obj) => {
  const list = [];
  for (const attr in obj) {
    list.push({ _id: attr, name: obj[attr] });
  }
  return list;
};

export const assignValIdsObjOption = (data) => {
  const newObj = {};
  for (const elFilter in data) {
    if (!Array.isArray(data[elFilter]) && data[elFilter]._id) {
      newObj[elFilter] = data[elFilter]._id;
    }
    if (Array.isArray(data[elFilter])) {
      newObj[elFilter] = newObj[elFilter].map((el) => el._id);
    }
  }
  return newObj;
};

export const shave100Nbr = (note, dividedBy = 1) => {
  const result = note / dividedBy;

  if (Number.isInteger(result)) {
    return result;
  }

  return Math.round(result);
};

/**
 * @param { Number } num
 * @param { Number | null } dec
 * @returns { Number }
 */
export const toFixedIfDecimal = (num, dec = 2) =>
  Math.round(num * Math.pow(10, dec)) / Math.pow(10, dec);

export const convertObjToDropdownCfg = (config) => {
  let dropdownConfig = [];
  if (typeof config === "object") {
    for (const key in config) {
      dropdownConfig.push({ _id: key, value: config[key], name: config[key] });
    }
  }
  return dropdownConfig;
};

export const sortNews = (newsData) => {
  let newsSorted = [];
  if (Array.isArray(newsData)) {
    newsSorted = newsData
      .filter((news) => news.status !== "draft")
      .sort((newsA, newsB) => +Boolean(newsB.pined) - +Boolean(newsA.pined));
  }
  return newsSorted;
};

export const getParentFolderByKeyAndOriginalName = (keyWithOriginalName) => {
  let url = keyWithOriginalName?.key;
  if (Boolean(url)) {
    if (url.endsWith("/")) {
      url = url.slice(0, -1);
    }
    let parts = url.split("/");
    parts[parts.length - 1] = "";
    if (parts.length) parts = parts.join("/");
    return parts;
  } else {
    return "";
  }
};

export const parseStringToFloat = (value) => Number.parseFloat(value) || 0;

export const truncateString = (str, withTooltip = false) => {
  return (
    <div className=" arh-w-full arh-truncate arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
      {withTooltip ? (
        // @ts-ignore
        <BlackTooltip
          title={str}
          placement="top"
        ></BlackTooltip>
      ) : (
        str
      )}
    </div>
  );
};

export const getAmount = (reimbursement) => {
  if (
    reimbursement?.rate?._id &&
    ["accepted", "waiting"].includes(reimbursement?.status) &&
    reimbursement?.type === "normal"
  ) {
    const tips = Number(reimbursement?.tips || 0);
    const cost = Number(reimbursement?.costExcludingTaxes || 0);
    const rateTotal = Number(reimbursement?.rate?.total || 0);

    const manualTaxes = reimbursement?.manualTaxes?.length
      ? reimbursement.manualTaxes.reduce(
        (sum, { taxe = 0 }) => sum + Number(taxe),
        0
      )
      : (cost * rateTotal) / 100;

    const amount = cost + tips + manualTaxes;

    return amount.toFixed(2);
  }

  return Number(reimbursement?.amount || 0).toFixed(2);
};

export const getAmountBeforeRate = (reimbursement) => {
  if (reimbursement?.rate?._id) {
    if (
      reimbursement?.type === "normal" &&
      ["accepted", "waiting"].includes(reimbursement?.status)
    ) {
      const cost = Number(reimbursement?.costExcludingTaxes || 0);

      const manualTaxes = reimbursement?.manualTaxes?.length
        ? reimbursement.manualTaxes.reduce(
          (sum, { taxe = 0 }) => sum + Number(taxe),
          0
        )
        : (cost * Number(reimbursement?.rate?.total || 0)) / 100;

      const amount = cost + manualTaxes;

      return amount.toFixed(2);
    }

    return Number(reimbursement?.amount || 0).toFixed(2);
  }

  return "0.00";
};

export const handleCountAssignation = (workingHours) => {
  let assignation = 0;
  if (workingHours?.length) {
    for (const data of workingHours) {
      let employerAssignment = data?.EmployerAssignment;
      if (employerAssignment?.assignementTitle) {
        assignation++;
      }
    }
  }
  return assignation;
};

export const handleCountDetails = (workingHours) => {
  let details = [...workingHours];
  if (details.length) {
    details = details.filter((workingHour) => workingHour.detail);
  }
  return details.length;
};

export const handleCountHoursTypes = (workingHours) => {
  let hoursTypes = [];
  if (workingHours?.length) {
    for (const data of workingHours) {
      let hourType = data?.WorkedHoursTypes?._id;
      if (hourType && !hoursTypes.includes(hourType)) hoursTypes.push(hourType);
    }
  }
  return hoursTypes.length;
};

export const sortArrayByAttrName = (arr, attrName) => {
  return arr.sort((a, b) => {
    if (
      typeof a[attrName] === "undefined" ||
      typeof b[attrName] === "undefined"
    ) {
      return 0;
    }
    const nameA = a[attrName].toUpperCase(); // ignore upper and lowercase
    const nameB = b[attrName].toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    // names must be equal
    return 0;
  });
};

export const isValidObjectIds = (_ids) => {
  try {
    return Array.isArray(_ids)
      ? _ids.map((_id) => new mongoose.Types.ObjectId(_id))
      : new mongoose.Types.ObjectId(_ids);
  } catch {
    return false;
  }
};

// copied from employeFiles.config.js
export const makeDefaultOpener = (messageList) => {
  const currentMsg = localStorage.getItem("current-msg");
  if (messageList.includes(currentMsg)) {
    return true;
  }

  return false;
};

export const secondsToHoursFloat = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const minuteStr = minutes < 10 ? `0${minutes}` : minutes;
  return parseFloat(`${hours || 0}.${minuteStr || 0}`).toFixed(2);
};

export const getTaskSourceModel = (
  { _id, parentModel, Project } = {
    _id: null,
    parentModel: null,
    Project: null
  }
) =>
  (Project && "Assignement") ||
  (parentModel && "Task") ||
  (_id === "formation" && "Formation") ||
  (_id && "Project") ||
  null;

export const convertStringToNestedProperty = (obj, str) => {
  // Split the input string into an array of keys using the dot as the separator
  const keys = str.split(".");
  // Initialize the result variable to the input object
  let result = obj;
  // Iterate through each key in the array
  for (const key of keys) {
    // Check if the property exists in the current result object
    if (result && result[key] !== undefined) {
      // If it exists, update the result to the value of the nested property
      result = result[key];
    } else {
      // If the property does not exist, set the result to undefined and break out of the loop
      result = undefined;
      break;
    }
  }
  // Return the final result, which represents the value of the nested property
  return result;
};

export const getNameRegOnRecrutment = (name) => {
  // Extraction des propriétés des filtres
  const normalized = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const nameParts = normalized.split(/\s+/);

  const buildRegex = (parts) => {
    let regex = "";
    while (parts.length > 0) {
      const part = parts.shift();
      if (part.toUpperCase() === "NOT") {
        regex += "(?!.*\\b" + parts.shift() + "\\b)";
      } else if (part.toUpperCase() === "AND") {
        regex += "(?=.*\\b" + parts.shift() + "\\b)";
      } else if (part.toUpperCase() === "OR") {
        regex += "|";
      } else if (part === "(") {
        regex += "(?:" + buildRegex(parts) + ")";
      } else if (part === ")") {
        break;
      } else {
        regex += `.*${part}`;
      }
    }
    return regex;
  };

  // Construction de l'expression régulière pour chaque partie du nom
  const nameRegex = buildRegex(nameParts);
  const nameReg = new RegExp(nameRegex, "i");
  return nameReg;
};

export const filterCandidats = (
  candidats,
  name,
  columns,
  tagIds,
  availability,
  nameReg,
  period = null
) => {
  // Filtrage de la liste de candidatures
  const filterList = candidats.filter((c) => {
    // Vérification des tags
    if (tagIds?.length && !c.tags?.some((t) => tagIds.includes(t._id))) {
      return false;
    }

    if (availability) {
      if (c.availability?.startDate && c.availability?.endDate) {
        if (
          !(
            moment(c.availability.startDate).isBefore(
              moment(availability.endDate)
            ) &&
            moment(availability.startDate).isBefore(
              moment(c.availability.endDate)
            )
          )
        ) {
          return false;
        }
      }
    }

    if (period) {
      if (
        !moment(new Date(c.createdAt)).isBetween(
          period.startDate,
          period.endDate
        )
      ) {
        return false;
      }
    }

    if (name && name.startsWith('"') && name.endsWith('"')) {
      return columns
        .map((column) =>
          convertStringToNestedProperty(c, column)?.replace(/\s+/g, " ")
        )
        .filter((value) => !_.isNil(value))
        .some(
          (value) =>
            value.toLowerCase() ===
            name.substring(1, name.length - 1).toLowerCase()
        );
    } else {
      // Construction de la source pour la recherche (nom, titre du poste, commentaires)
      const source = columns
        .map((column) => convertStringToNestedProperty(c, column))
        .join(" ");
      // Test de l'expression régulière sur la source normalisée
      if (
        nameReg &&
        !nameReg.test(source.normalize("NFD").replace(/[\u0300-\u036f]/g, ""))
      ) {
        return false;
      }
    }
    return true;
  });
  return filterList;
};

export const getMimeFileType = (fileType) => {
  switch (fileType) {
    case ".avi":
      return "video/x-msvideo";
    case ".mp4":
      return "video/mp4";
    case ".mkv":
      return "video/x-matroska";
    case ".mov":
      return "video/quicktime";
    case ".wmv":
      return "video/x-ms-wmv";
    case ".flv":
      return "video/x-flv";
    case ".webm":
      return "video/webm";
    case ".mpeg":
    case ".mpg":
      return "video/mpeg";
    case ".3gp":
      return "video/3gpp";
    case ".ogv":
      return "video/ogg";
    case ".m4v":
      return "video/x-m4v";
    case ".mts":
    case ".m2ts":
      return "video/MP2T";
    case ".ts":
      return "video/MP2T";
    case ".divx":
      return "video/divx";
    case ".xvid":
      return "video/xvid";
    default:
      return "";
  }
};

/**
 *
 * @param {string} dateA
 * @param {string} dateB
 * @param {"asc" | "desc"} sortOrder
 * @returns
 */
export function sortDate(dateA, dateB, sortOrder = "asc") {
  const dateMomentA = moment(dateA, "YYYY-MM-DD", true);
  const dateMomentB = moment(dateB, "YYYY-MM-DD", true);

  if (dateMomentA.isValid() && dateMomentB.isValid()) {
    if (sortOrder === "asc") {
      return dateMomentA.diff(dateMomentB);
    }

    return dateMomentB.diff(dateMomentA);
  }

  if (dateMomentA.isValid()) {
    return sortOrder === "asc" ? 1 : -1;
  }

  if (dateMomentB.isValid()) {
    return sortOrder === "asc" ? -1 : 1;
  }

  return 0;
}

export const customTooltips = (tooltipModel, chartRef, t) => {
  // don't forget to setup the legend in dataset
  // Tooltip Element
  let tooltipEl = document.getElementById("chartjs-tooltip");

  // Create element on first render
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.innerHTML = "<div style='position:relative;'></div>";
    tooltipEl.style.pointerEvents = "none";
    document.body.appendChild(tooltipEl);
  }

  // Hide if no tooltip
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.transitionTimingFunction = "ease-out";
    tooltipEl.style.transitionDelay = "10ms";
    // @ts-ignore
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set caret Position
  tooltipEl.classList.remove("above", "below", "no-transform");
  if (tooltipModel.yAlign) {
    tooltipEl.classList.add(tooltipModel.yAlign);
  } else {
    tooltipEl.classList.add("no-transform");
  }

  const getBody = (bodyItem) => bodyItem.lines;

  // Set Text
  if (tooltipModel.body) {
    // const {xAlign} = tooltipModel
    let style =
      "position: absolute; top:-2px; left:35%; border-right: 10px solid transparent; border-left:10px solid transparent; border-bottom: 10px solid rgba(0,0,0,0.78)";

    // if(xAlign==="right") style = "position: absolute; top:30%; right:-2px; border-left: 10px solid rgba(0,0,0,0.78); border-top:10px solid transparent; border-bottom:10px solid transparent"
    const bodyLines = tooltipModel.body.map(getBody);
    const data = bodyLines[0][0]?.split(":");
    let innerHtml = `<div style="${style}"></div><div style="background: rgba(0, 0, 0, 0.78); z-index:2; border-radius:4px; padding:4px; color:white; font-family:Poppins;font-size:12px; display:flex;  flex-direction: column; border:1px solid transparent;"><div>${t(data[0].trim())}</div><div>${data[1]}</div></div>`;
    tooltipEl.innerHTML = innerHtml;
  }

  let position =
    chartRef?.current?.chartInstance?.canvas.getBoundingClientRect();
  // console.log({ position, chartRef })
  // Display, position, and set styles for font
  // @ts-ignore
  tooltipEl.style.opacity = 1;
  // @ts-ignore
  tooltipEl.style.zIndex = 60;
  tooltipEl.style.position = "absolute";
  tooltipEl.style.left =
    10 + position.left + window.pageXOffset + tooltipModel.caretX + "px";
  tooltipEl.style.top =
    position.top + window.pageYOffset + tooltipModel.caretY + "px";
  tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
  tooltipEl.style.fontSize = tooltipModel.bodyFontSize + "px";
  tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
  tooltipEl.style.padding =
    tooltipModel.yPadding + "px " + tooltipModel.xPadding + "px";
  // tooltipEl.style.transitionTimingFunction = 'ease-in'
  // tooltipEl.style.transitionDelay = '10ms'

  tooltipEl.style.transform = "translate(-50%, 0)";
  tooltipEl.style.transition = "all .1s ease";
  tooltipEl.style.pointerEvents = "none";
};

export function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

export function flatNestedArrayOfObjects(arrayObjs, attrName) {
  var result = [];
  if (Array.isArray(arrayObjs) && arrayObjs?.length > 0) {
    arrayObjs.forEach(function (a) {
      result.push(a);
      if (Array.isArray(a[attrName]) && a[attrName].length > 0) {
        const childs = flatNestedArrayOfObjects(a[attrName], attrName);
        result = result.concat(childs);
      }
    });
  }
  return result;
}

export function removeGroupAncestorsWhenChildPresent(groupes) {
  const childs = groupes.filter(
    (group) => Array.isArray(group.ancestors) && group.ancestors.length > 0
  );
  if (childs.length === 0) {
    return groupes;
  }
  const childAncestors = childs.reduce((ancestors, group) => {
    return ancestors.concat(group.ancestors);
  }, []);

  return groupes.filter((group) => !childAncestors.includes(group._id));
}

const formatFlatGroup = ({ _id, title, ancestors }) => ({
  _id,
  title,
  ancestors
});
const getGroupesWithPropertie = (filterGroups, groupes) => {
  if (!Array.isArray(filterGroups)) return [];
  if (filterGroups[0]?._id) return filterGroups;
  const flattedGroups = flatNestedArrayOfObjects(groupes, "subgroupes");
  const flattedGroupList = flattedGroups.map((group) => formatFlatGroup(group));
  return flattedGroupList.filter((group) => filterGroups.includes(group?._id));
};
export function isGroupIdsMatchFilter(element, groupesFilter, groupes = []) {
  if (groupes.length) {
    groupesFilter = getGroupesWithPropertie(groupesFilter, groupes);
  }
  if (!Array.isArray(groupesFilter) || groupesFilter.length === 0) return true;
  if (!Array.isArray(element.groupIds) || element.groupIds.length === 0)
    return false;

  groupesFilter = removeGroupAncestorsWhenChildPresent(groupesFilter);

  const groupIdsIntersectAncestors = (groupIds, group) => {
    if (!Array.isArray(group.ancestors) || !Array.isArray(groupIds))
      return false;
    return group.ancestors.some((ancestorId) => groupIds.includes(ancestorId));
  };
  return groupesFilter.some(
    (groupFilter) =>
      element.groupIds.includes(groupFilter._id || groupFilter) ||
      groupIdsIntersectAncestors(element.groupIds, element)
  );
}

export function formatSelectedGroupsForFilter(selectedGroups) {
  if (!Array.isArray(selectedGroups)) {
    return [];
  }
  if (!selectedGroups?.[0]?._id) {
    return selectedGroups;
  }
  return selectedGroups.map((group) => group?._id);
}

export function formatGroupForInput(value) {
  return value;
}

export function formatGroupForFilter(groups) {
  if (!Array.isArray(groups)) return [];
  if (!groups?.[0]?._id) {
    return groups;
  }
  return groups.map((group) => group?._id);
}

export function getLinkLegende(text) {
  const linkRegex =
    /(https?:\/\/)[\w\-~]+(\.[\w\-~]+)+(\/[\w\-~@:%]*)*(\.[\w\-~]+)+(\/[\w\-~@:%]*)*(#[\w]*)?(\?[^\s]*)?/gi;
  const matches = text.match(linkRegex);
  const links = new Set();
  if (matches && matches.length > 0) {
    for (const match of matches) {
      try {
        new URL(match);
        links.add(match);
      } catch (error) {
        continue;
      }
    }
  }
  return Array.from(links);
}

export const checkErrors = (errorArray, type = "required") =>
  errorArray.some((err) => err.type === type);

export const applyFavIcon = (favIcon, reset = true) => {
  const cacheFavIcon = localStorage.getItem("favIcon");
  const links_icon = document.querySelectorAll("link[rel*='icon']");
  const image = document.querySelector(
    "link[rel*='apple-touch-startup-image']"
  );
  // @ts-ignore
  let links = [...links_icon, image];
  if (cacheFavIcon && !reset) {
    var { location, mimetype } = JSON.parse(cacheFavIcon);
  }
  if (favIcon) {
    if (reset) {
      localStorage.removeItem("favIcon");
    } else if (location !== favIcon.location) {
      localStorage.setItem(
        "favIcon",
        JSON.stringify({
          location: favIcon.location,
          mimetype: favIcon.mimetype
        })
      );
    }
    links.forEach((link) => {
      // @ts-ignore
      link.type = favIcon.mimetype;
      // @ts-ignore
      link.href = favIcon.location;
    });
  } else if (location) {
    links.forEach((link) => {
      // @ts-ignore
      link.type = mimetype;
      // @ts-ignore
      link.href = location;
    });
  }
};

export const clearStorage = () => {
  // sessionStorage.clear() peut impacter le fonctionnement de certains modules.
  localStorage.clear();
};

export const truncateTextPlaceholder = (str, maxLength) => {
  return str.length > maxLength ? str.slice(0, maxLength) + "..." : str;
};

export const fetchFavicon = async (link) => {
  const domain = new URL(link).hostname;
  const size = 32;
  try {
    const faviconUrl = `https://${domain}/favicon.ico`;

    // Charger l'image
    const img = new Image();
    img.src = faviconUrl;

    // Attendre que l'image soit chargée (ou échoue)
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    if (img.naturalWidth < size) {
      return `https://www.google.com/s2/favicons?sz=${size}&domain=${domain}`;
    }
    return faviconUrl;
  } catch (error) {
    return `https://www.google.com/s2/favicons?sz=${size}&domain=${domain}`;
  }
};
//   const size = 32;

//   try {
//     // 1. Validation et extraction du domaine
//     let domain;
//     try {
//       // Ajout du protocole si absent pour éviter les erreurs d'URL
//       const urlString = link.includes('://') ? link : `https://${link}`;
//       const url = new URL(urlString);
//       domain = url.hostname;
//     } catch (urlError) {
//       // Si l'URL est invalide, on utilise une fallback
//       return `https://www.google.com/s2/favicons?sz=${size}`;
//     }

//     // 2. Tentative avec favicon.ico
//     const faviconUrl = `https://${domain}/favicon.ico`;

//     // Utilisation de fetch pour vérifier l'existence et les dimensions
//     const response = await fetch(faviconUrl, {
//       method: 'HEAD',
//       mode: 'no-cors' // Mode no-cors pour éviter les problèmes CORS
//     }).catch(() => null);

//     // 3. Si fetch échoue ou retourne une erreur, on tente avec l'image
//     if (!response) {
//       // Fallback: utilisation de l'API Google
//       return `https://www.google.com/s2/favicons?sz=${size}&domain=${domain}`;
//     }

//     // 4. Vérification des dimensions de l'image
//     return new Promise((resolve) => {
//       const img = new Image();

//       // Configuration des gestionnaires d'événements
//       img.onload = () => {
//         // Vérification de la taille minimale
//         if (img.naturalWidth >= size && img.naturalHeight >= size) {
//           resolve(faviconUrl);
//         } else {
//           resolve(`https://www.google.com/s2/favicons?sz=${size}&domain=${domain}`);
//         }
//       };

//       img.onerror = () => {
//         resolve(`https://www.google.com/s2/favicons?sz=${size}&domain=${domain}`);
//       };

//       // Définir un timeout pour éviter les blocages
//       const timeoutId = setTimeout(() => {
//         img.src = ''; // Annuler le chargement
//         resolve(`https://www.google.com/s2/favicons?sz=${size}&domain=${domain}`);
//       }, 5000); // Timeout de 5 secondes

//       img.onload = img.onerror = () => clearTimeout(timeoutId);

//       // Chargement de l'image
//       img.src = faviconUrl;
//     });

//   } catch (error) {
//     // Fallback général en cas d'erreur
//     return `https://www.google.com/s2/favicons?sz=${size}`;
//   }
// };

export const isValidUrl = (url) => {
  try {
    return Boolean(new URL(url));
  } catch (e) {
    return false;
  }
};

export const calculateHoursTotalBilling = (minutesTotal, hourlyBilling) => {
  const _amount = (minutesTotal * hourlyBilling) / 60;
  return typeof _amount === "number" ? _amount.toFixed(2) : 0;
};

export const getDefaultHolidayLabels = ({
  holidayList = [],
  language,
  employeeId = ""
}) => {
  const defaultCategories = [MALADIE, VACANCE, ABSENCE];
  return holidayList
    .filter(
      (holiday) =>
        defaultCategories.includes(holiday.category) &&
        !holiday.desactivated &&
        holiday.CombinedEmployees?.includes(employeeId)
    )
    .map((label) => ({
      designation:
        language === "fr" ? label.designationFr : label.designationEn,
      category: language === "fr" ? label.categoryFr : label.categoryEn,
      type: label.category,
      information:
        language === "fr" ? label.informationFr : label.informationEn,
      notAllowNegativeValue: Boolean(label.notAllowNegativeValue),
      unlimitedBalance: Boolean(label.unlimitedBalance),
      hideEmployeeCounter: Boolean(label.hideEmployeeCounter),
      annualRenewalDate: label.annualRenewalDate,
      carryForwardRemainingDays: Boolean(label.carryForwardRemainingDays),
      autoDeductHolidaysEnabled: Boolean(label.autoDeductSpecialDays),
      hourlyRateExport: label.hourlyRateExport
    }))
    .reduce((acc, item) => {
      acc[item.type] = item;
      return acc;
    }, {});
};

export const getEditableOtherHolidays = ({
  holidayList = [],
  holidaySolde = [],
  employeeId = ""
}) => {
  const defaultCategories = [MALADIE, VACANCE, ABSENCE];
  return (
    holidayList
      ?.filter(
        (holiday) =>
          !defaultCategories.includes(holiday.category) &&
          (!holiday.nethris ||
            (holiday.nethris && holiday.nethrisFormatExport === "amount")) &&
          !holiday.desactivated &&
          holiday.CombinedEmployees?.includes(employeeId)
      )
      ?.map((holiday) => {
        const foundSolde = holidaySolde.find(
          (solde) => solde.holidayName === holiday._id
        );
        return {
          ...holiday,
          hours:
            foundSolde?.hours !== null && foundSolde?.hours !== undefined
              ? foundSolde.hours
              : "",
          hoursRemaining:
            foundSolde?.hoursRemaining !== null &&
              foundSolde?.hoursRemaining !== undefined
              ? foundSolde.hoursRemaining
              : "",
          hourlyRateExport:
            foundSolde?.hourlyRateExport !== null &&
              foundSolde?.hourlyRateExport !== undefined
              ? foundSolde.hourlyRateExport
              : ""
        };
      }) || []
  );
};

export const getCategoryLabel = (
  holidayList = [],
  category,
  language = "fr"
) => {
  const holidayLabel = holidayList.find((holiday) => {
    const valueCategory = holiday.category || holiday._id;
    return valueCategory === category;
  });
  return holidayLabel?._id
    ? language === "fr"
      ? holidayLabel?.categoryFr
      : holidayLabel?.categoryEn
    : "";
};

export const getOtherHolidays = ({
  holidayList = [],
  language,
  employeeId = ""
}) => {
  const defaultCategories = [MALADIE, VACANCE, ABSENCE];
  return holidayList
    .filter(
      (holiday) =>
        !defaultCategories.includes(holiday.category) &&
        !holiday.desactivated &&
        holiday.CombinedEmployees?.includes(employeeId)
    )
    .map((holiday) => ({
      _id: holiday._id,
      designation:
        language === "fr" ? holiday.designationFr : holiday.designationEn,
      category: language === "fr" ? holiday.categoryFr : holiday.categoryEn,
      hours: holiday.hours || 0,
      hoursRemaining: holiday.hoursRemaining || 0,
      nethris: holiday.nethris || false,
      referenceCode: holiday.referenceCode || "",
      information:
        language === "fr" ? holiday.informationFr : holiday.informationEn,
      notAllowNegativeValue: Boolean(holiday.notAllowNegativeValue),
      hideEmployeeCounter: Boolean(holiday.hideEmployeeCounter),
      unlimitedBalance: Boolean(holiday.unlimitedBalance)
    }));
};

export const convertToNumber = (str) => {
  let number = str;
  if (typeof number === "string") {
    const normalizedStr = str.replace(",", ".");
    number = parseFloat(normalizedStr);
  }
  return isNaN(number) ? 0 : number;
};

export const formatNethrisVal = (
  bankHoliday,
  holidayName = {},
  hoursPerDay,
  isToFixed = false
) => {
  let hourVal = 0,
    outVal = 0,
    formattedVal = "";
  let from = holidayName.nethrisFormatReceived;
  let to = holidayName.nethrisFormatVisible;
  const lang = JSON.parse(localStorage.getItem("user"))?.language ?? "fr";
  let employeRate = convertToNumber(bankHoliday.EMPLOYEE_RATE);
  hoursPerDay = hoursPerDay ? parseFloat(hoursPerDay) : 8;
  from = from ?? "hours";

  const convertToNbr2 = (str) => {
    let nbr = convertToNumber(str);
    nbr = nbr.toFixed(2);
    const nethrisFormat = nbr ? nbr.replace(".", ",") : "0,00";
    return isToFixed ? nethrisFormat : parseFloat(nbr);
  };
  // Always convert value into hour
  if (employeRate) {
    if (from === "dollars") {
      const amount = bankHoliday?.BALANCE_AMOUNT
        ? convertToNumber(bankHoliday.BALANCE_AMOUNT)
        : 0;
      hourVal = amount / employeRate;
    } else {
      hourVal = bankHoliday?.BALANCE_HOURS
        ? convertToNumber(bankHoliday.BALANCE_HOURS)
        : 0;
    }

    // Calculate value format based on hour
    if (to === "hours") {
      outVal = hourVal;
      outVal = convertToNbr2(outVal);
      formattedVal = `${outVal} h`;
    } else if (to === "days") {
      outVal = hourVal / hoursPerDay;
      outVal = convertToNbr2(outVal);
      if (lang === "fr") {
        // formattedVal = outVal <= 1 ? `${outVal} jour` : `${outVal} jours`;
        formattedVal = `${outVal} j`;
      } else if (lang === "en") {
        // formattedVal = outVal <= 1 ? `${outVal} day` : `${outVal} days`;
        formattedVal = `${outVal} d`;
      }
    } else if (to === "dollars") {
      outVal = hourVal * employeRate;
      outVal = convertToNbr2(outVal);
      formattedVal = lang === "fr" ? `${outVal} $` : `$ ${outVal}`;
    }
  } else {
    if (to === "dollars") {
      const amount = bankHoliday?.BALANCE_AMOUNT
        ? convertToNumber(bankHoliday.BALANCE_AMOUNT)
        : 0;
      outVal = convertToNbr2(amount);
      formattedVal = lang === "fr" ? `${outVal} $` : `$ ${outVal}`;
    } else {
      outVal = bankHoliday?.BALANCE_HOURS
        ? convertToNumber(bankHoliday.BALANCE_HOURS)
        : 0;
      outVal = convertToNbr2(outVal);
      formattedVal = `${outVal} h`;
    }
  }
  return { val: outVal, formattedVal };
};

export const getHolidayCategories = ({
  holidayList = [],
  language,
  employeList = []
}) => {
  const colors = {
    [VACANCE]: "arh-bg-[#609cd3]",
    [ABSENCE]: "arh-bg-[#f5d954]",
    [MALADIE]: "arh-bg-[#f67c53]"
  };

  return holidayList
    .filter((holiday) => {
      if (holiday.desactivated) {
        return false;
      } else {
        return employeList.every((employeeId) =>
          holiday.CombinedEmployees?.includes(employeeId)
        );
      }
    })
    .map(({ category, _id, categoryFr, categoryEn, nethris = false }) => ({
      value: category || _id,
      title: language === "fr" ? categoryFr : categoryEn,
      color: category
        ? colors[category]
        : nethris
          ? "arh-bg-gray-700"
          : "arh-bg-green-700"
    }));
};

export const capitalizeSentences = (paragraph) => {
  return paragraph
    .split(". ")
    .map((sentence) => sentence.charAt(0).toUpperCase() + sentence.slice(1))
    .join(". ");
};

// extract number from string
export const extractNumberFromStr = (str) =>
  str ? str.replace(/[^0-9]/g, "") : 0;

// Search Handling
export const escapeRegExp = (string) =>
  string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const getCongeInDayAndHour = (conge, hourPerDay = 8) => {
  if (!conge?.end || !conge?.start) return { day: 0, hour: 0 };
  const conge24Hours = moment(conge.end).diff(conge.start, "hours", true);
  let congeDays = Math.floor(conge24Hours / 24),
    congeLastDayHour = conge24Hours % 24;
  if (congeLastDayHour > hourPerDay) {
    congeDays++;
    congeLastDayHour = 0;
  }
  return { day: congeDays, hour: congeLastDayHour };
};

export const getWeekendsInPeriod = (startDate, endDate) => {
  let currentDate = moment(startDate);
  const endDateMoment = moment(endDate);
  if (!currentDate.isValid() || !endDateMoment.isValid()) {
    return [];
  }
  const weekends = [];

  while (currentDate.isSameOrBefore(endDateMoment, "day")) {
    if (currentDate.day() === 6 || currentDate.day() === 0) {
      weekends.push(currentDate.format("DD/MM"));
    }
    currentDate.add(1, "day");
  }

  return weekends;
};

export const getCongeInHours = (conge, hourPerDay = 8) => {
  const { decreaseValue: congeDecreaseValue, quotient: congeQuotien } =
    conge || {};
  const decreaseValue =
    congeDecreaseValue || getCongeInDayAndHour(conge, hourPerDay);
  const quotient = congeQuotien || 0;
  return (decreaseValue.day * hourPerDay + decreaseValue.hour) * quotient;
};

export function getDaysBetweenTwoDates(startDate, endDate) {
  let days = [];
  let currentDate = moment(startDate);
  let lastDate = moment(endDate);

  while (currentDate <= lastDate) {
    days.push({
      date: currentDate.format("DD/MM/YYYY"),
      jour: currentDate.format("dddd")
    });
    currentDate.add(1, "days");
  }
  return days;
}

const getRandomColorHex = () => {
  const randomValue = Math.floor(Math.random() * 16777215).toString(16);
  return "#" + randomValue;
};

// get most common element in an array
export const mode = (array) => {
  if (array.length == 0) return null;
  var modeMap = {};
  var maxEl = array[0],
    maxCount = 1;
  for (var i = 0; i < array.length; i++) {
    var el = array[i];
    if (modeMap[el] == null) modeMap[el] = 1;
    else modeMap[el]++;
    if (modeMap[el] > maxCount) {
      maxEl = el;
      maxCount = modeMap[el];
    }
  }
  return maxEl;
};

export function setSimpleBackgroundColors(
  data = [],
  colorSets = [],
  attribut = "type"
) {
  data = data.map((item, index) => ({
    _id: item.name,
    [attribut]: item.name,
    backgroundColor: colorSets?.[index] || getRandomColorHex()
  }));
  return [
    ...data,
    {
      _id: NOT_INFORMED,
      [attribut]: NOT_INFORMED,
      backgroundColor: COLOR_NO_INFORMATION
    }
  ];
}

export function isValidHourFormat(time) {
  // validation format date "HH:mm"
  const regex = /^([01]\d|2[0-3]):[0-5]\d$/;
  return regex.test(time);
}

export function onTimepickerRef(timepickerRef, handleBlur) {
  if (timepickerRef && timepickerRef.input) {
    timepickerRef.input.onblur = () => {
      handleBlur(timepickerRef.input.value);
    };

    // Ajout du focus scroll vers l'heure sélectionnée
    timepickerRef.input.onclick = () => {
      // Petit délai pour attendre que la popup soit affichée
      setTimeout(() => {
        const list = document.querySelector(".react-datepicker__time-list");
        if (list) {
          const selectedItem = list.querySelector(
            ".react-datepicker__time-list-item--selected"
          );
          if (selectedItem) {
            selectedItem.scrollIntoView({ block: "center" });
          }
        }
      }, 50); // Peut être ajusté si nécessaire
    };
  }
}

export function adjustMidnightHours(date) {
  const formattedDate = moment(date).format("mm:ss");
  const ajustedDate =
    formattedDate === "59:59"
      ? moment(date).add(1, "seconds").startOf("minute").toDate()
      : date;
  return ajustedDate;
}

export function getRenamedFiles(oldKey, newKey, files) {
  let key = newKey ? newKey : oldKey;
  let data_files = [];
  const tempFiles = files.map((file) => {
    if (file.key === oldKey) {
      const fileToEdit = {
        _id: file.id,
        key: key,
        originalName: key.split("/")[key.split("/").length - 2],
        wasabiName: key.split("/")[key.split("/").length - 2],
        modified: moment().valueOf(),
        relativeKey: key
      };
      data_files.push(fileToEdit);
      file = {
        ...file,
        ...fileToEdit
      };
    } else if (file.key.indexOf(oldKey) === 0) {
      const fileToEdit = {
        _id: file.id,
        key: file.key.replace(oldKey, key),
        modified: moment().valueOf(),
        relativeKey: key
      };
      data_files.push(fileToEdit);
      file = {
        ...file,
        ...fileToEdit
      };
    }
    return file;
  });
  return { data_files, tempFiles };
}

export function onDeleteDocStorage(key, files) {
  const tempFiles = files.filter((file) => file.key.indexOf(key) === -1);
  const filesToDelete = files.filter((file) => file.key.indexOf(key) === 0);
  filesToDelete.forEach((file) => {
    DocumentStorage.deleteById(file.id)
      .then()
      .catch((error) => {
        console.error(error);
      });
  });
  return tempFiles;
}

function getFolderKeyName(key) {
  if (key?.length && key[key.length - 1] !== "/") key += "/";
  return key;
}

export function getCreateFiles(newFiles, prefix, files) {
  const tempFiles = newFiles.map((newFile) => {
    let newKey = getFolderKeyName(prefix);
    newKey += newFile.name;
    let file = files.find((tempFile) => tempFile.key === newKey);
    if (!file) {
      file = {
        key: newKey,
        size: parseInt(newFile.size),
        modified: moment().valueOf()
      };
    }
    return file;
  });
  return tempFiles;
}

export function mergeWithoutDuplicatesId(prev, next) {
  const nextIds = new Set(next.map((item) => item._id));
  const uniquePrev = prev.filter((item) => !nextIds.has(item._id));
  return [...uniquePrev, ...next];
}

export function formatTextFromTextArea(text) {
  if (!text) return "";

  // Normaliser retours à la ligne
  return text.split(/\r?\n/).map((line, index, arr) => (
    // eslint-disable-next-line
    <React.Fragment key={index}>
      {line.replace(/\t/g, "    ")}
      {index < arr.length - 1 && <br />}
    </React.Fragment>
  ));
}

export function businessDuration(
  startDate,
  endDate,
  daysOff = [],
  autoDeductHolidaysEnabled = false
) {
  let start = moment(startDate);
  let end = moment(endDate);

  if (end.isBefore(start)) {
    return 0; // si la date de fin est avant la date de début
  }

  let daysOffCount = 0;
  if (autoDeductHolidaysEnabled) {
    forEach(daysOff, ({ date }) => {
      if (moment(date).isBetween(start, end, "day", "[]")) {
        daysOffCount++;
      }
    });
  }

  let days = 0;
  // parcourir jour par jour
  while (start.isSameOrBefore(end, "day")) {
    const day = start.day(); // 0 = dimanche, 6 = samedi
    if ((day !== 0 && day !== 6) || !autoDeductHolidaysEnabled) {
      days++;
    }
    start.add(1, "day");
  }

  return days - daysOffCount;
}

export function getListFiltered(filters, filteredList, groupes) {
  // if (!filters) {
  //   filters = {
  //     Employer: [].join(","),
  //     emplacements: [].join(","),
  //     groups: [].join(","),
  //     // actualise,
  //     startDate: moment().startOf("month").toDate(),
  //     endDate: moment().endOf("month").toDate()
  //   };
  // } else {
  //   filters = {
  //     ...filters,
  //     Employer: filters.employees.join(","),
  //     emplacements: filters.emplacements.join(","),
  //     groups: filters.groups
  //   };
  // }
  if (filters?.emplacements?.length) {
    // let emplacements = filters.emplacements.split(",");
    // filteredList = filteredList.filter((employeHour) => {
    //   for (let emplacement of emplacements) {
    //     if (employeHour.emplacement?.[0]?.includes(emplacement)) {
    //       return employeHour;
    //     }
    //   }
    // });
    filteredList = filteredList.filter((employeHour) =>
      filters.emplacements.some((_emplacementId) =>
        employeHour.emplacement?.[0]?.includes(_emplacementId)
      )
    );
  }
  // if (filters?.Employer) {
  if (filters?.employees?.length) {
    // let employees = filters.Employer.split(",");
    filteredList = filteredList.filter((employeHour) =>
      // employees.includes(employeHour.idEmployer)
      filters?.employees.includes(employeHour.idEmployer)
    );
  }
  if (filters?.groups?.length) {
    filteredList = filteredList.filter((element) =>
      isGroupIdsMatchFilter(element, filters.groups, groupes)
    );
  }
  if (!filters.showInactiveEmployeesData) {
    filteredList = filteredList.filter(
      (element) => element.status !== DEFINITIVEMENT_INACTIF
    );
  }
  return filteredList;
}

export function getMapSelectedEmployeHours(
  employeSelectedHours,
  selectedTotal,
  selectedFormationHours,
  selectedWorkingHours
) {
  let output = [];
  const nameCount = {};

  for (let employe of employeSelectedHours) {
    let originalName = employe.personnalInformation.nameLower;
    let employeName = employe.personnalInformation.name;

    if (nameCount[originalName]) {
      employeName = `${employe.personnalInformation.name} (${nameCount[originalName]})`;
      nameCount[originalName]++;
    } else {
      nameCount[originalName] = 1;
    }

    output.push({
      key: employe._id,
      dataset: getData(
        { ...employe.recap },
        employe.agenda,
        selectedTotal,
        selectedFormationHours,
        selectedWorkingHours
      ),
      name: employeName
    });
  }

  return output;
}

const getData = (
  dataRecap = {},
  dataAgenda = {},
  selectedTotal,
  selectedFormationHours,
  selectedWorkingHours
) => {
  let dataWorkingHours = [],
    dataTotalHours = [],
    dataFormations = [],
    tempDataAgenda = dataAgenda,
    { hoursAssigned, hoursNotAssigned, formationsHours } =
      dataRecap; /*?.hoursAssigned?.filter(
        (item) => item.title !== "Formation",
      )*/
  if (hoursAssigned?.length) {
    dataTotalHours = getAssignedTotalHours(hoursAssigned);
    dataWorkingHours = getAllAssignedWorkedHours(hoursAssigned);
  }
  if (hoursNotAssigned?.length) {
    const notAssignedHours = hoursNotAssigned.map(
      ({ notAssigned, ...rest }) => ({ hours: notAssigned, ...rest })
    );
    dataWorkingHours = dataWorkingHours.concat(notAssignedHours);
  }
  if (formationsHours?.length) {
    const hours = formationsHours.map(({ formation, ...rest }) => ({
      hours: formation,
      project: "Formation",
      ...rest
    }));
    dataWorkingHours = dataWorkingHours.concat(hours);
  }

  const {
    workingHourWithPlannedHours,
    scheduledHoursWithoutWork: withoutWork
  } = injectPlannedHours(dataWorkingHours, tempDataAgenda);
  dataWorkingHours = workingHourWithPlannedHours;
  const scheduledWithoutWorkDuration = [];
  withoutWork
    .filter((hour) => moment(hour.end).diff(hour.start, "hours", true) > 0)
    .forEach((hour) => {
      const duration = moment(hour.end).diff(hour.start, "hours", true);

      const existing = scheduledWithoutWorkDuration.find((item) =>
        moment(item.startDate).isSame(hour.start, "day")
      );

      if (existing) {
        existing.planedHours += duration;
      } else {
        scheduledWithoutWorkDuration.push({
          startDate: moment(hour.start).startOf("day").toDate(),
          planedHours: duration
        });
      }
    });
  dataWorkingHours = dataWorkingHours.concat(scheduledWithoutWorkDuration);

  dataWorkingHours = dataWorkingHours.sort((a, b) =>
    moment(a.startDate).diff(new Date(b.startDate))
  );

  dataFormations = dataRecap.formationsHours
    .concat(dataRecap.hoursNotAssigned)
    .reduce((groups, game) => {
      const date = moment
        .tz(
          game.startDate,
          game.Timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
        )
        .format("DD/MM/YYYY");
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(game);
      return groups;
    }, {});
  dataFormations = Object.values(dataFormations)
    .map((formation) => {
      let data = {
        notAssigned: 0,
        formation: 0
      };
      for (let dataF of formation) {
        data.notAssigned += dataF.notAssigned || 0;
        data.formation += dataF.formation || 0;
        if (!data.startDate) data.startDate = dataF.startDate;
      }
      return data;
    })
    .sort((a, b) => moment(a.startDate).diff(new Date(b.startDate)));

  dataWorkingHours = mapDataWithValues(dataWorkingHours, selectedWorkingHours);

  dataTotalHours = mapDataWithValues(dataTotalHours, selectedTotal);
  dataFormations = mapDataWithValues(dataFormations, selectedFormationHours);

  let dataReturned = [
    {
      columns: selectedWorkingHours,
      data: dataWorkingHours
    },
    {
      ySteps: 3, //will put space of 3 rows
      columns: selectedTotal,
      data: dataTotalHours
    },
    {
      ySteps: 3, //will put space of 3 rows
      columns: selectedFormationHours,
      data: dataFormations
    }
  ];

  return dataReturned;
};

const getAssignedTotalHours = (hoursAssigned) => {
  let assignedTotalHours = [];
  for (const _hourAssigned of hoursAssigned) {
    if (_hourAssigned?.childElements?.length) {
      for (const _child of _hourAssigned.childElements) {
        // let hoursBilling = calculateHoursTotalBilling(
        //   _child.hours,
        //   _child.hourlyBilling
        // );
        let hoursBilling = _child.hours * _child.hourlyBilling || 0;
        assignedTotalHours.push({
          title: _hourAssigned.title,
          assignementTitle: _child.title,
          hourlyBilling: hoursBilling ? hoursBilling.toFixed(2) : "",
          hours: _child.hours
        });
      }
    }
  }
  return assignedTotalHours;
};

const getAllAssignedWorkedHours = (hoursAssigned, tempDataAgenda = []) => {
  let assignedWorkedHours = [];
  for (const _hourAssigned of hoursAssigned) {
    if (_hourAssigned?.WorkingHours?.length) {
      assignedWorkedHours = assignedWorkedHours.concat(
        _hourAssigned.WorkingHours
      );
    }
    if (_hourAssigned?.childElements?.length) {
      const childWorkedHours = getAllAssignedWorkedHours(
        _hourAssigned.childElements,
        tempDataAgenda
      );
      assignedWorkedHours = assignedWorkedHours.concat(childWorkedHours);
    }
  }
  return assignedWorkedHours;
};

const injectPlannedHours = (allWorkingHours, tempDataAgenda) => {
  const workingHourWithPlannedHours = allWorkingHours.map((itemHour) => {
    const foundAgenda = tempDataAgenda.filter((agenda) => {
      return !(
        moment(agenda.end).isBefore(itemHour.startDate) ||
        moment(agenda.start).isAfter(itemHour.endDate)
      );
    });
    tempDataAgenda = tempDataAgenda.filter((agenda) => {
      return (
        moment(agenda.end).isBefore(itemHour.startDate) ||
        moment(agenda.start).isAfter(itemHour.endDate)
      );
    });
    let duration = 0;
    for (let hour of foundAgenda) {
      duration += moment(hour.end).diff(hour.start, "hours", true);
    }
    itemHour["planedHours"] = duration;
    return itemHour;
  });

  return {
    workingHourWithPlannedHours,
    scheduledHoursWithoutWork: tempDataAgenda
  };
};

const mapDataWithValues = (data, selectedItems) => {
  return data.length
    ? data.map((record) =>
      selectedItems.map((selected) => ({
        value: getValue(selected, record) || ""
      }))
    )
    : [generateEmptyData(selectedItems)];
};

const getValue = (head, col) => {
  let valueReturn = "";
  const timezone = col.Timezone
    ? col.Timezone
    : Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!head.isHour) {
    let valueGeted = get(col, head.value);
    // if (!valueGeted && head.value === "startDate")
    //   valueGeted = get(col, "start");
    if (valueGeted) {
      if (head.isDate) {
        valueReturn = moment.tz(valueGeted, timezone).format("DD/MM/YYYY");
      } else {
        if (!head.horaire) {
          valueReturn =
            head.value === "hours" ? toFixedIfDecimal(valueGeted) : valueGeted;
        } else {
          if (head.isArrayOfObject) {
            let total = 0;
            for (let child of valueGeted) {
              total += child[head.attribut];
            }
            valueReturn = timeConvert(total * 60);
          } else {
            valueReturn = timeConvert(valueGeted * 60);
          }
        }
      }
    }
  } else {
    valueReturn =
      col.startDate && col.endDate
        ? `${moment.tz(col.startDate, timezone).format("HH:mm")} - ${moment
          .tz(adjustMidnightHours(col.endDate), timezone)
          .format("HH:mm")}`
        : "";
  }
  return valueReturn;
};

const generateEmptyData = (selectedItems) => {
  return selectedItems.map(() => ({ value: "" }));
};

const timeConvert = (num) => {
  let hours = 0,
    minutes = 0;
  if (!isNaN(Number(num))) {
    hours = Math.floor(num / 60);
    minutes = Math.round(num % 60);

    if (minutes === 60) {
      minutes = 0;
      hours += 1;
    }
  }
  return `${twoDigit(hours)}:${twoDigit(minutes)}`;
};

export const getUpdatedSections = (changedFields, fieldToSection) => {
  const sections = new Set();

  Object.keys(changedFields).forEach((field) => {
    const section = fieldToSection[field];
    if (section) {
      sections.add(section);
    }
  });

  return Array.from(sections);
};

export function hasDuplicateAttribute(array, attribute) {
  const values = array.map((item) => item[attribute]);
  return values.length !== new Set(values).size;
}

export function getEmployeesFromGroups(groups, selectedGroups) {
  const uniqueEmployers = new Set();
  const selectedSet = new Set(selectedGroups);

  // Fonction pour trouver les groupes feuilles sélectionnés (sans enfants sélectionnés)
  function findLeafSelectedGroups(groupList) {
    const leafGroups = [];

    function traverse(group) {
      const hasSelectedChildren =
        group.subgroupes &&
        group.subgroupes.some(
          (child) => selectedSet.has(child._id) || traverse(child)
        );

      if (selectedSet.has(group._id) && !hasSelectedChildren) {
        leafGroups.push(group);
      }

      // Continuer à traverser même si ce groupe n'est pas sélectionné
      if (group.subgroupes) {
        group.subgroupes.forEach(traverse);
      }
    }

    groupList.forEach(traverse);
    return leafGroups;
  }

  // Fonction pour collecter tous les employés d'un groupe (incluant ses sous-groupes)
  function collectAllEmployees(group) {
    // Employés directs du groupe
    (group.memberEmployes || []).forEach((member) => {
      if (member.Employer?._id) {
        uniqueEmployers.add(String(member.Employer._id));
      }
    });

    // Employés de tous les sous-groupes
    (group.subgroupes || []).forEach((subgroup) => {
      collectAllEmployees(subgroup);
    });
  }

  // Trouver les groupes feuilles sélectionnés
  const leafSelectedGroups = findLeafSelectedGroups(groups);

  // Collecter tous les employés de ces groupes
  leafSelectedGroups.forEach((group) => {
    collectAllEmployees(group);
  });

  return Array.from(uniqueEmployers);
}

export function getComputedAuthorizedEmployees({
  selectedGroups,
  locations,
  selectedLocations,
  selectedEmployees,
  employees
}) {
  const selectedGroupIds = selectedGroups || [];
  const emplacementIds = new Set(
    (selectedLocations || []).map(({ _id }) => String(_id))
  );

  const employeesFromGroups = selectedGroupIds?.length ? employees
    .filter(
      (employee) =>
        Array.isArray(employee.groupIds) &&
        isGroupIdsMatchFilter(employee, selectedGroupIds)
    ).map(({ id }) => String(id)) : [];
  const employeesFromEmplacements = (locations || [])
    .filter((e) => emplacementIds.has(String(e._id)))
    .flatMap(
      (e) =>
        e?.memberEmployees
          ?.map((emp) => String(emp?._id || emp))
          .filter(Boolean) || []
    );

  const combinedEmployeeIds = [
    ...(selectedEmployees || []).map(({ id }) => String(id)),
    ...employeesFromGroups,
    ...employeesFromEmplacements
  ];
  return Array.from(new Set(combinedEmployeeIds)) || [];
}

export function setNestedProperty(obj, path, value) {
  const parts = Array.isArray(path) ? path : path.split(".");
  let current = obj;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (!current[part] || typeof current[part] !== "object") {
      current[part] = {};
    }
    current = current[part];
  }

  current[parts[parts.length - 1]] = value;
  return obj;
}

export function formatTextPeriod({ dateStart, endDate, status, icon, article1, article2 }) {
  const divStyle = "arh-w-[6rem] arh-flex arh-justify-between";
  const articleStyle = "arh-text-left arh-w-fit";
  const widthDate = "arh-w-[4.6rem] arh-text-right";
  const parseLocalDate = (str) => {
    if (!str) return null;
    const [year, month, day] = str.split('-').map(Number);
    return new Date(year, month - 1, day);
  };
  const start = parseLocalDate(dateStart);
  const end = parseLocalDate(endDate);
  const formatStartDate = isValid(start) ? format(start, 'dd/MM/yyyy') : "-";
  const formatEndDate = isValid(end) ? format(end, 'dd/MM/yyyy') : "-";
  return (
    status ?
      <>
        <div className={divStyle}>
          <div className={articleStyle}></div>
          <div className={widthDate}>{formatStartDate}</div>
        </div>
        <div className={divStyle}></div>
        <img alt="calendar_carrer" src={icon} />
      </>
      :
      <>
        <div className={divStyle}>
          <div className={articleStyle}>{article1}</div>
          <div className={widthDate}>{formatStartDate}</div>
        </div>
        <div className={divStyle}>
          <div className={articleStyle}>{article2}</div>
          <div className={widthDate}>{formatEndDate}</div>
        </div>
        <img alt="calendar_carrer" src={icon} />
      </>
  )
}